import type { EventsKey } from 'ol/events';
import type Geometry from 'ol/geom/Geometry';
import { unByKey } from 'ol/Observable';
import { AppStore, createInitialState } from './core/store';
import { EditHistory } from './core/history';
import { HistoryController } from './ui/HistoryController';
import { genericSelectionChanged, labelsFollowSelection, selectionOnlyChange } from './core/stateChanges';
import type { AppState, BasemapId, CasurHydricReference, CasurHydricVisibility, EntityViewLevel, HybridReferenceVisibility, LabelMode, MapTool, SelectionEntry, VectorLayerModel } from './core/types';
import { createLayerClassification, createMasterClassification } from './domain/classification';
import { activePartialExceptionField } from './domain/mixedThematic';
import { emptyWorkScope, workScopeFragmentIds } from './domain/scope';
import { deriveDisplaySet } from './domain/displaySet';
import { countEntitiesForFragments, entityViewFragmentIds } from './domain/entityView';
import { drillDownTarget, type NavigationTarget } from './domain/navigation';
import { Breadcrumb } from './ui/Breadcrumb';
import { GeometryEditToolbar } from './ui/GeometryEditToolbar';
import { ExcelRelationDialog } from './ui/ExcelRelationDialog';
import { ClassifyPane } from './ui/ClassifyPane';
import { LegendDesignPane } from './ui/LegendDesignPane';
import { ScenarioPane } from './ui/ScenarioPane';
import { FilterBar } from './ui/FilterBar';
import { EntitySearchBox } from './ui/EntitySearchBox';
import { themeTargetIds } from './domain/themeTarget';
import { captureLabelTarget } from './domain/labelTarget';
import { createMasterLayer, normalizeLayerOrder } from './domain/layers';
import { emptySelectionSet, masterEntryForFragment } from './domain/selectionSet';
import { importVectorLayer } from './io/vectorImport';
import { inspectShapefiles, ShapefileCrsRequiredError, type CrsPendingDataset, type ManualShapefileCrs, type ShapefileDatasetSummary } from './io/shapefileImport';
import { importSuccessMessage, importSummaryHtml, readCrsChoices, renderCrsChoices } from './ui/shapefileImportView';
import { importGeoPackage, inspectGeoPackage, type GeoPackageInspection } from './io/geopackageImport';
import { masterEligibility, masterModelFromVectorLayer } from './io/masterImport';
import { applyAssignment, parseFieldValue } from './domain/operationalFields';
import { createOperationalSubarea, createRemainingSubareaPreview, overlapAreaM2, SUBAREA_LAYER_ID, synchronizeSubareaLayer, type SubareaParent, type SubareaPreview } from './domain/operationalSubareas';
import { MapController } from './map/MapController';
import { DrawingController } from './map/DrawingController';
import { PlanningGeometryEditor } from './map/PlanningGeometryEditor';
import { DIFFERENCE_DRAW_ASSISTANCE, type DifferenceDrawAssistState } from './map/DifferenceDrawingAssist';
import { buildBoundaryCutPlan, type BoundaryCutPlan } from './domain/boundaryCut';
import { LiveMeasure } from './map/LiveMeasure';
import { MeasurementController } from './map/MeasurementController';
import { SelectionController } from './map/SelectionController';
import { PartialAreaController } from './map/PartialAreaController';
import { alignLabelConfigToEntity, defaultClassificationConfig, defaultLegendConfig } from './presentation/config';
import { AttributeTable } from './ui/AttributeTable';
import { Explorer } from './ui/Explorer';
import { LayerManager } from './ui/LayerManager';
import { PresentationEditor } from './ui/PresentationEditor';
import { PropertiesPanel } from './ui/PropertiesPanel';
import { WorkingLayerPanel } from './ui/WorkingLayerPanel';
import { PlanningEditController } from './ui/PlanningEditController';
import { PlanningPanel } from './ui/PlanningPanel';
import { ScopePanel } from './ui/ScopePanel';
import { MapLegend } from './ui/MapLegend';
import { SubareaPanel } from './ui/SubareaPanel';
import { SuerteSplitController } from './ui/SuerteSplitController';
import { TopographyExportController } from './ui/TopographyExportController';
import { WorkspaceController } from './ui/WorkspaceController';
import { ShellController, type ShellCommand } from './ui/ShellController';
import { entityNoun, StatusBar } from './ui/StatusBar';
import { DemSource } from './elevation/demSource';
import { elevationAtMapCoordinate, formatElevationReading } from './elevation/elevationQuery';
import { ElevationOverlay } from './map/ElevationOverlay';
import { ElevationProfilePanel } from './ui/ElevationProfilePanel';
import { ElevationQueryTool } from './ui/ElevationQueryTool';
import { ElevationSourceInfo } from './ui/ElevationSourceInfo';
import { MapExportController } from './ui/MapExportController';
import { MapExportService } from './export/MapExportService';
import { layerExportSet, masterExportSet, safeFileName, toCsv, toGeoJson } from './export/dataExport';
import { LabelPositionController } from './map/LabelPositionController';
import { MapLocationController } from './map/MapLocationController';
import { formatCoordinate, mapCoordinateFor, type GeoCoordinate } from './domain/coordinates';
import { copyText } from './ui/clipboard';
import { LocationCard } from './ui/LocationCard';
import { MapContextMenu, type MapContextAction } from './ui/MapContextMenu';
import { WorkspaceLayoutController } from './ui/WorkspaceLayoutController';
import { loadWorkspaceLayoutPreference } from './ui/workspaceLayout';
import { summarizeFeatureSelection, summarizeMasterSelection } from './domain/selectionSummary';
import { renderShell } from './ui/shell';
import { LabelDragInteraction } from './map/LabelDragInteraction';
import { icon } from './ui/icons';
import { ProjectController } from './ui/ProjectController';
import { PwaController } from './pwa/PwaController';
import { registerPwa } from './pwa/registerPwa';
import { importCasurHydricReference } from './hydric/casurHydricReference';
import { IndexedDbCasurHydricRepository } from './hydric/casurHydricStorage';
import { isPickerCancellation } from './project/fileSystemAccess';
import {
  createRememberedMasterSource,
  IndexedDbMasterSourceRepository,
  loadMasterAutoloadPreference,
  masterRestoreAction,
  pickMasterSource,
  saveMasterAutoloadPreference,
  type MasterRestoreAction,
  readMasterSource,
  supportsMasterSourcePicker,
  type MasterSourceBrowser,
  type MasterSourceFileHandle,
  type RememberedMasterSource,
} from './master/masterSource';
import {
  beginGeometryEdit,
  applyDifferenceFragment,
  deriveActiveLayerModel,
  emptyGeometryEditHistory,
  entityFragmentIds,
  excludeFragment,
  fragmentAreaHa,
  geometryEditTargetForSelection,
  geometryEntityMetrics,
  previewDifferenceFragment,
  redoGeometryEdit,
  sameGeometryEditHistory,
  undoGeometryEdit,
  withActiveMasterModel,
  type DifferenceFragmentPreview,
} from './domain/geometryEditing';

const required = <T extends Element>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`No se encontró ${selector}`);
  return element;
};
const labelModeForLevel = (level: EntityViewLevel): LabelMode => level === 'finca' ? 'fincas' : level === 'suerte' ? 'suerte' : level === 'tablon' ? 'tablones' : 'fragmentos';
const esc = (value: string): string => value.replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char] ?? char);

export class App {
  private readonly store = new AppStore(createInitialState(undefined, loadWorkspaceLayoutPreference()));
  /** V1.9.0 E5: global Deshacer / Rehacer (core/history). */
  private readonly history = new EditHistory();
  private readonly mapController: MapController;
  private readonly selectionController: SelectionController;
  private readonly measurementController: MeasurementController;
  private readonly drawingController: DrawingController;
  private readonly partialAreaController: PartialAreaController;
  private readonly suerteSplit: SuerteSplitController;
  private readonly topographyExport: TopographyExportController;
  private readonly explorer: Explorer;
  private readonly layerManager: LayerManager;
  private readonly propertiesPanel: PropertiesPanel;
  private readonly attributeTable: AttributeTable;
  private readonly presentationEditor: PresentationEditor;
  private readonly workingLayerPanel: WorkingLayerPanel;
  private readonly planningPanel: PlanningPanel;
  private readonly planningEdit: PlanningEditController;
  private readonly scopePanel: ScopePanel;
  private readonly mapLegend: MapLegend;
  private readonly subareaPanel: SubareaPanel;
  private readonly workspaceController: WorkspaceController;
  private readonly shellController: ShellController;
  private readonly statusBar: StatusBar;
  /** Copernicus GLO-30 clip, loaded once from static assets; read-only and independent of the project. */
  private readonly dem = new DemSource();
  private readonly layoutController: WorkspaceLayoutController;
  private readonly labelDrag: LabelDragInteraction;
  private readonly elevationQuery: ElevationQueryTool;
  private readonly elevationProfile: ElevationProfilePanel;
  private readonly breadcrumb: Breadcrumb;
  private readonly geometryEditToolbar: GeometryEditToolbar;
  private readonly classifyPane: ClassifyPane;
  private readonly legendDesignPane: LegendDesignPane;
  private readonly scenarioPane: ScenarioPane;
  private readonly filterBar: FilterBar;
  private readonly mapExportController: MapExportController;
  private readonly labelPositionController: LabelPositionController;
  private readonly mapLocation: MapLocationController;
  private readonly casurHydricRepository = new IndexedDbCasurHydricRepository();
  private readonly masterSourceRepository = new IndexedDbMasterSourceRepository();
  private pendingShapefileFiles: File[] = [];
  private pendingGeoPackageFile: File | null = null;
  private pendingImportedLayers: VectorLayerModel[] = [];
  private pendingImportSummaries: ReadonlyMap<string, ShapefileDatasetSummary> = new Map();
  private pendingImportNotices: readonly string[] = [];
  private pendingShapefileCrs: Readonly<Record<string, ManualShapefileCrs>> = {};
  private pendingSubareaPreview: SubareaPreview | null = null;
  private pendingDifferencePreview: DifferenceFragmentPreview | null = null;
  private pendingBoundaryCutPlan: BoundaryCutPlan | null = null;
  private boundaryCutEventKeys: EventsKey[] = [];
  private differenceDrawMode: 'free' | 'boundary' | null = null;
  private boundaryCutStarted = false;
  private boundaryCutStartCoordinate: readonly number[] | null = null;
  private boundaryCutEndCoordinate: readonly number[] | null = null;
  private differenceStartedSession = false;
  private pendingCasurHydricFile: File | null = null;
  private pendingCasurHydricReference: CasurHydricReference | null = null;
  private pendingMasterSourceHandle: MasterSourceFileHandle | null = null;
  private pendingMasterSourceFile: File | null = null;
  private pendingMasterSourceLayer: string | null = null;
  /** V1.9.0 E5: recovery of the remembered Maestro (notice on the map) and the project GeoStudio opened with. */
  private masterRestore: MasterRestoreAction = 'none';
  private masterRecovering = false;
  /**
   * Source operations that can bring a Maestro: every import the user starts takes the next number, so a recovery of
   * the remembered Maestro still reading its file sees it was superseded and leaves the state alone (the same token
   * pattern as the elevation profile).
   */
  private sourceOperation = 0;
  private readonly initialProjectId: string;
  /** The next file chosen in the import input was asked for as the Maestro (quick action without a file picker). */
  private masterIntent = false;
  private rememberedMasterSource: RememberedMasterSource | null = null;
  private masterSourceAvailability: 'loading' | 'none' | 'ready' | 'permission-required' | 'denied' | 'unavailable' | 'unsupported' = 'loading';
  private masterSourceChanged = false;
  private masterSourceError: string | null = null;

  constructor(root: HTMLElement) {
    this.initialProjectId = this.store.getState().project.id;
    renderShell(root);
    this.store.attachHistory(this.history, (patch, state) => this.completeHistoryPatch(patch, state));
    this.mapController = new MapController(required('#map'), this.store);
    this.layoutController = new WorkspaceLayoutController(this.store, () => this.mapController.updateSize());
    this.labelDrag = new LabelDragInteraction(this.mapController.map);
    new ProjectController(this.store, this.mapController.map, () => this.restoreCasurHydricReference(true));
    registerPwa(new PwaController(this.store, document, window, () => this.mapController.refreshBasemaps()));
    this.selectionController = new SelectionController(this.mapController.map, this.store);
    // V1.9.3 H: one live-measure card for every drawing tool (a map overlay: never saved, never in the PNG).
    const liveMeasure = new LiveMeasure(this.mapController.map);
    this.measurementController = new MeasurementController(this.mapController.map, (text) => { required('#measurement-status').textContent = text; }, liveMeasure);
    this.drawingController = new DrawingController(this.mapController.map, this.store, (text) => {
      required('#drawing-status').textContent = text;
      if (this.planningPanel?.isDrawing) required('#planning-status').textContent = text;
    }, liveMeasure);
    this.partialAreaController = new PartialAreaController(this.store, this.drawingController, (preview) => this.showSubareaPreview(preview), (text) => { required('#partial-area-status').textContent = text; });
    this.suerteSplit = new SuerteSplitController(this.store, this.drawingController, this.mapController, (text) => { required('#partial-area-status').textContent = text; });
    this.topographyExport = new TopographyExportController(this.store, this.drawingController, this.mapController, (blob, filename) => {
      const url = URL.createObjectURL(blob); const anchor = document.createElement('a');
      anchor.href = url; anchor.download = filename; document.body.append(anchor); anchor.click(); anchor.remove(); window.setTimeout(() => URL.revokeObjectURL(url), 0);
    });
    this.explorer = new Explorer(required('#entity-tree'), (kind, id) => this.focusEntity(kind, id, true), document.querySelector<HTMLInputElement>('#tree-search'));
    this.layerManager = new LayerManager(required('#layer-list'), this.store, {
      zoom: (id) => this.mapController.zoomToLayer(id),
      openTable: () => this.openAttributeTable(),
      openStyle: () => this.shellController.openPane('layer-style'),
      exportLayer: (id) => this.runDataExport('geojson', id),
    }, required<HTMLElement>('#layer-style-pane'));
    this.propertiesPanel = new PropertiesPanel(required('#properties'), this.store, {
      drillDown: (kind, id) => this.drillDown(kind, id),
      zoom: () => this.zoomToCurrentSelection(),
      focusEntity: (kind, id) => this.focusEntity(kind, id),
      selectionAction: (action) => this.runSelectionAction(action),
      focusMember: (entry) => { this.selectionController.focusEntry(entry, true); this.mapController.zoomToSelection(); },
      editPlanningGeometry: (layerId, featureId) => this.planningEdit.start(layerId, featureId),
      appearance: {
        preview: (preview) => this.mapController.setPlanningAppearancePreview(preview),
        labelDrag: (onDrag) => this.setLabelDrag(onDrag),
        notify: (text) => this.notify(text),
      },
    });
    this.attributeTable = new AttributeTable(required('#attribute-table'), required('#table-count'), (id, layerId, master, entityLevel) => {
      if (!layerId) return;
      const state = this.store.getState();
      if (master && state.geometryEditSession && state.model) {
        const session = state.geometryEditSession;
        if (entityFragmentIds(state.model, session.entityKind, session.entityId).includes(id)) {
          this.store.update({ geometryEditSession: { ...session, selectedFragmentId: id } }, { markDirty: false });
        }
        return;
      }
      const entry: SelectionEntry | null = master
        ? entityLevel ? { source: 'master', layerId, kind: entityLevel, id } : masterEntryForFragment(state.model, id, state.entityViewLevel)
        : { source: 'generic', layerId, kind: 'feature', id };
      if (entry) this.selectionController.focusEntry(entry, true);
    }, document.querySelector<HTMLElement>('#table-tools'), document.querySelector<HTMLElement>('#table-title'));
    this.presentationEditor = new PresentationEditor(this.store);
    new ExcelRelationDialog(this.store, (summary) => this.notify(summary));
    this.classifyPane = new ClassifyPane(document, this.store);
    this.legendDesignPane = new LegendDesignPane(document, this.store);
    this.scenarioPane = new ScenarioPane(document, this.store);
    this.filterBar = new FilterBar(required('#filter-chips'), required('#filter-popover'), this.store);
    this.workingLayerPanel = new WorkingLayerPanel(required('#working-tools'), this.store, (tool) => this.activateTool(tool));
    this.planningEdit = new PlanningEditController(required('#planning-edit-toolbar'), this.store, new PlanningGeometryEditor(this.mapController.map), {
      setEditTarget: (target) => this.mapController.setPlanningEditTarget(target),
      status: (text) => { required('#planning-status').textContent = text; this.notify(text); },
      showProperties: () => this.shellController.openPane('properties'),
    });
    this.planningPanel = new PlanningPanel(required('#planning-tools'), this.store, this.drawingController, () => this.shellController.openPane('properties'), () => this.planningEdit.cancel());
    this.scopePanel = new ScopePanel(required('#scope-panel'), this.store);
    this.mapLegend = new MapLegend(required('#map-legend'), this.store);
    this.subareaPanel = new SubareaPanel(required('#subarea-tools'), this.store, (tool) => this.partialAreaController.begin(tool), (entry) => this.selectionController.focusEntry(entry, false), (parent) => this.showRemainingSubareaPreview(parent));
    this.shellController = new ShellController(document, this.store, {
      command: (command, source) => this.runCommand(command, source),
      viewLevel: (level) => this.setEntityViewLevel(level),
    });
    new HistoryController(this.store, this.history, {
      local: () => {
        const session = this.store.getState().geometryEditSession;
        if (session) return { name: 'la edición de geometría', canUndo: session.history.cursor > 0, canRedo: session.history.cursor < session.history.commands.length, undo: () => this.handleGeometryAction('undo'), redo: () => this.handleGeometryAction('redo') };
        if (this.planningEdit.isEditing) return { name: 'la edición de vértices', canUndo: this.planningEdit.canUndoStep, canRedo: false, undo: () => this.planningEdit.undoStep(), redo: () => {} };
        return null;
      },
      blocked: () => this.propertiesPanel.appearanceEditor.dirty ? 'Aplica o cancela primero los cambios de Apariencia.' : null,
      notify: (message) => this.notify(message),
    });
    const elevationOverlay = new ElevationOverlay(this.mapController.map);
    const elevationInfo = new ElevationSourceInfo(this.dem, () => {
      const state = this.store.getState();
      return [
        ...(state.model ? [{ id: state.model.id, name: 'Maestro CASUR', unit: 'fragments' as const, geometries: state.model.fragments.flatMap(({ feature }) => feature.getGeometry() ?? []) }] : []),
        ...state.layers.filter((layer) => layer.kind === 'generic' || layer.kind === 'working').map((layer) => ({ id: layer.id, name: layer.name, geometries: layer.features.flatMap(({ feature }) => feature.getGeometry() ?? []) })),
      ];
    });
    const elevationText = (coordinate: readonly number[]): string => this.dem.status === 'error' ? 'Sin dato (modelo no disponible)' : formatElevationReading(elevationAtMapCoordinate(this.dem, coordinate));
    this.elevationProfile = new ElevationProfilePanel(required('#elevation-profile'), this.store, this.dem, this.drawingController, elevationOverlay, () => elevationInfo.open(), () => { this.planningEdit.cancel(); this.elevationQuery.deactivate(false); });
    this.elevationQuery = new ElevationQueryTool(this.mapController.map, this.store, this.dem, elevationOverlay, required('#elevation-readout'), () => elevationInfo.open(), () => { this.elevationProfile.cancelDrawing(); this.planningEdit.cancel(); });
    document.querySelectorAll<HTMLElement>('[data-elevation-tool]').forEach((button) => button.addEventListener('click', () => {
      if (button.dataset.elevationTool === 'query') this.elevationQuery.toggle(); else this.elevationProfile.toggle();
    }));
    this.statusBar = new StatusBar(document, this.mapController.map, undefined, { read: elevationText, onChange: (listener) => { this.dem.onChange(listener); }, openSource: () => elevationInfo.open() });
    // Load the DEM when the main thread is idle after start-up (precached by the service worker for offline use);
    // any elevation tool loads it immediately if it is needed sooner.
    const whenIdle = window.requestIdleCallback ?? ((callback: () => void) => window.setTimeout(callback, 1_200));
    whenIdle(() => void this.dem.load(), { timeout: 4_000 });
    this.geometryEditToolbar = new GeometryEditToolbar(required('#geometry-edit-toolbar'), (action) => this.handleGeometryAction(action));
    // Temporary map location: ephemeral UI state, independent of agricultural selection and of the project.
    this.mapLocation = new MapLocationController(this.mapController.map);
    const locationCard = new LocationCard(required('#map-location-card'), (action) => {
      const location = this.mapLocation.current; if (!location) return;
      if (action === 'copy') void this.copyCoordinate(location);
      else if (action === 'zoom') this.mapLocation.zoomTo();
      else this.mapLocation.clear();
    }, (location) => elevationText(mapCoordinateFor(location)));
    this.dem.onChange(() => { if (this.mapLocation.current) locationCard.render(this.mapLocation.current); });
    this.mapLocation.onChange((location) => locationCard.render(location));
    new MapContextMenu(this.mapController.map, () => this.canOpenMapContextMenu(), (action, coordinate) => this.runMapContextAction(action, coordinate));
    new EntitySearchBox(required<HTMLInputElement>('#entity-search'), required('#entity-search-results'), () => this.store.getState().model, (result) => this.focusEntity(result.kind, result.id, true), (coordinate) => this.markLocation(coordinate, true));
    this.breadcrumb = new Breadcrumb(required('#breadcrumb'), (target) => this.navigateTo(target));
    this.labelPositionController = new LabelPositionController(this.mapController.map, this.store, this.mapController.getLabelLayer(), (text) => { required('#label-position-status').textContent = text; });
    this.mapExportController = new MapExportController(this.store, new MapExportService(this.mapController.map, (state, labels, renderScale = 1) => {
      this.mapController.renderComposition(state, labels, renderScale);
    }));
    this.workspaceController = new WorkspaceController(this.store, {
      resize: () => this.mapController.updateSize(),
    });
    this.bindEvents();
    this.store.subscribe((state, previous) => {
      if (state.subareas !== previous.subareas || state.operationalFields !== previous.operationalFields) {
        const layers = synchronizeSubareaLayer(state.layers, state.subareas, state.operationalFields);
        if (layers !== state.layers) {
          const subareaLayer = layers.find(({ id }) => id === SUBAREA_LAYER_ID);
          let classificationConfig = state.classificationConfig;
          if (subareaLayer && classificationConfig.layerId === SUBAREA_LAYER_ID && classificationConfig.field) {
            const ids = themeTargetIds(subareaLayer, classificationConfig.themeTarget);
            classificationConfig = createLayerClassification(subareaLayer, classificationConfig, ids);
          }
          this.store.update({ layers, classificationConfig, thematicSchemes: state.thematicSchemes.map((item) => item.id === classificationConfig.id ? classificationConfig : item) }, { markDirty: false });
          // The nested update already synced the new layers; continue with the latest state so a stale
          // `state.layers` (without the subarea layer) can never be synced back onto the map below.
          state = this.store.getState();
        }
      }
      if ((state.subareas !== previous.subareas || state.operationalFields !== previous.operationalFields) && state.model && activePartialExceptionField(state.classificationConfig, state.model, state.entityViewLevel, state.operationalFields)) {
        // Mixed Suerte + Áreas parciales view: a new partial value is a new category of the same legend.
        const masterLayer = state.layers.find(({ id }) => id === state.model!.id);
        const refreshed = masterLayer ? createMasterClassification(state.model, { ...state.classificationConfig, level: state.entityViewLevel }, themeTargetIds(masterLayer, state.classificationConfig.themeTarget), state.operationalFields) : null;
        if (refreshed && refreshed.categories.map(({ value }) => value).join('\u0000') !== state.classificationConfig.categories.map(({ value }) => value).join('\u0000')) {
          this.store.update({ classificationConfig: refreshed, thematicSchemes: state.thematicSchemes.map((item) => item.id === refreshed.id ? refreshed : item) }, { markDirty: false });
          state = this.store.getState();
        }
      }
      if (state.project.id !== previous.project.id) this.mapLocation.clear();
      if (state.model !== previous.model) this.mapController.setModel(state.model);
      if (state.model !== previous.model || state.geometryEditSession !== previous.geometryEditSession) this.syncDifferenceDrawingTarget(state);
      if (state.model !== previous.model || state.workScope !== previous.workScope || state.attributeFilters !== previous.attributeFilters || state.operationalFields !== previous.operationalFields || state.entityViewLevel !== previous.entityViewLevel || state.geometryEditSession !== previous.geometryEditSession) this.mapController.setMasterDisplay(state);
      if (state.activeTool !== previous.activeTool) {
        this.selectionController.setTool(state.activeTool);
        this.measurementController.setTool(state.activeTool);
        this.drawingController.setTool(state.activeTool);
        this.labelPositionController.setTool(state.activeTool);
      }
      if (state.selection !== previous.selection) this.mapController.setSelection(state.selection);
      const selectionChanged = state.selection !== previous.selection || state.selectionSet !== previous.selectionSet;
      // Working/external layers only restyle when their own selection changed (a Maestro selection never touches them).
      if (state.layers !== previous.layers || state.workScope !== previous.workScope || state.attributeFilters !== previous.attributeFilters || state.operationalFields !== previous.operationalFields || genericSelectionChanged(state, previous) || state.classificationConfig !== previous.classificationConfig || state.subareaLabelsVisible !== previous.subareaLabelsVisible || state.entityViewLevel !== previous.entityViewLevel || state.geometryEditSession !== previous.geometryEditSession || state.presentationWorkspace.showLabels !== previous.presentationWorkspace.showLabels) this.mapController.syncLayers(state);
      if (state.basemap !== previous.basemap || state.presentationWorkspace.showBasemap !== previous.presentationWorkspace.showBasemap) this.mapController.setBasemap(state.presentationWorkspace.showBasemap ? state.basemap : 'none');
      if (state.hybridReference !== previous.hybridReference) this.mapController.setHybridReference(state.hybridReference);
      if (state.casurHydricReference !== previous.casurHydricReference || state.casurHydricVisibility !== previous.casurHydricVisibility || state.casurHydricSelection !== previous.casurHydricSelection) this.mapController.syncCasurHydricReference(state);
      if (
        state.model !== previous.model || state.workScope !== previous.workScope
        || state.styleConfig !== previous.styleConfig || state.classificationConfig !== previous.classificationConfig || state.attributeFilters !== previous.attributeFilters
        || state.operationalFields !== previous.operationalFields || state.basemap !== previous.basemap || state.presentationWorkspace !== previous.presentationWorkspace || state.labelConfig !== previous.labelConfig
        || state.geometryEditSession !== previous.geometryEditSession || state.entityViewLevel !== previous.entityViewLevel
        || (state.subareas !== previous.subareas && Boolean(state.classificationConfig.partialExceptions?.enabled))
      ) this.mapController.setPresentation(state);
      else if (selectionChanged) this.mapController.updateSelection(state);
      if (
        state.model !== previous.model || state.labelMode !== previous.labelMode
        || state.workScope !== previous.workScope || state.attributeFilters !== previous.attributeFilters
        || state.labelConfig !== previous.labelConfig || state.classificationConfig !== previous.classificationConfig
        || state.savedSelections !== previous.savedSelections || state.operationalFields !== previous.operationalFields || state.presentationWorkspace.showLabels !== previous.presentationWorkspace.showLabels || state.entityViewLevel !== previous.entityViewLevel
        || (selectionChanged && labelsFollowSelection(state))
      ) this.mapController.setLabels();
      if (state.workScope !== previous.workScope) this.mapController.zoomToScope();
      this.render(selectionOnlyChange(state, previous) ? 'selection' : 'full');
    });
    this.mapController.syncCasurHydricReference(this.store.getState());
    this.render();
    void this.restoreCasurHydricReference(false);
    void this.restoreMasterSource();
  }

  private bindEvents(): void {
    const input = required<HTMLInputElement>('#import-file-input');
    required('#import-data').addEventListener('click', () => { this.masterIntent = false; input.click(); });
    input.addEventListener('change', () => { const files = [...(input.files ?? [])]; if (files.length) this.beginImport(files); input.value = ''; });
    // «Cargar Maestro CASUR» / «Cambiar Maestro» (Capas panel and empty map): the same import flow, never a parallel one.
    document.addEventListener('click', (event) => {
      const quick = (event.target as Element).closest<HTMLElement>('[data-master-quick]');
      if (quick) void this.quickMaster(quick.dataset.masterQuick as 'load' | 'change' | 'reload');
    });
    required('#label-move-done').addEventListener('click', () => this.propertiesPanel.appearanceEditor.finishLabelDrag());
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && this.labelDrag.active) { event.preventDefault(); this.propertiesPanel.appearanceEditor.finishLabelDrag(); } }, true);
    required('#select-master-source').addEventListener('click', () => void this.chooseMasterSource());
    required('#change-master-source').addEventListener('click', () => void this.chooseMasterSource());
    required<HTMLInputElement>('#master-autoload').addEventListener('change', (event) => saveMasterAutoloadPreference((event.target as HTMLInputElement).checked));
    required('#map-toast').addEventListener('click', (event) => {
      const action = (event.target as Element).closest<HTMLElement>('[data-master-restore]')?.dataset.masterRestore;
      if (action === 'recover') void this.recoverMaster(true);
      else if (action === 'choose') { this.masterRestore = 'none'; this.renderMasterRestore(); void this.quickMaster('change'); }
      else if (action === 'dismiss') { this.masterRestore = 'none'; this.renderMasterRestore(); }
    });
    required('#load-master-source').addEventListener('click', () => void this.loadRememberedMasterSource());
    required('#properties').addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-geometry-action]');
      if (button) this.handleGeometryAction(button.dataset.geometryAction ?? '');
    });
    required('#cancel-exclude-fragment').addEventListener('click', () => required<HTMLDialogElement>('#exclude-fragment-dialog').close());
    required('#apply-exclude-fragment').addEventListener('click', () => this.applyFragmentExclusion());
    required('#cancel-difference-fragment').addEventListener('click', () => this.cancelDifferencePreview());
    required('#apply-difference-fragment').addEventListener('click', () => this.applyDifferencePreview());
    required('#difference-mode-free').addEventListener('click', () => { required<HTMLDialogElement>('#difference-input-dialog').close(); this.beginDifferenceDrawing('free'); });
    required('#difference-mode-boundary').addEventListener('click', () => { required<HTMLDialogElement>('#difference-input-dialog').close(); this.beginDifferenceDrawing('boundary'); });
    required('#cancel-difference-input').addEventListener('click', () => required<HTMLDialogElement>('#difference-input-dialog').close());
    required('#boundary-cut-candidate-a').addEventListener('click', () => this.selectBoundaryCutCandidate(0));
    required('#boundary-cut-candidate-b').addEventListener('click', () => this.selectBoundaryCutCandidate(1));
    required('#cancel-boundary-cut-choice').addEventListener('click', () => this.cancelBoundaryCutChoice());
    required('#difference-snap-toggle').addEventListener('click', () => {
      const current = this.drawingController.differenceAssistState();
      if (current) this.drawingController.setDifferenceSnapEnabled(!current.snapEnabled);
    });
    required('#difference-trace-toggle').addEventListener('click', () => {
      const current = this.drawingController.differenceAssistState();
      if (current) this.drawingController.setDifferenceTraceEnabled(!current.traceEnabled);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const state = this.store.getState();
      const target = event.target as HTMLElement | null;
      if (state.presentationCaptureMode || state.geometryEditSession || document.querySelector('dialog[open]') || target?.closest('input, textarea, select')) return;
      if (state.selectionSet.entries.length || state.selection || state.genericSelection) this.selectionController.clear();
    });
    const drop = required<HTMLElement>('.app-shell');
    drop.addEventListener('dragover', (event) => { event.preventDefault(); drop.classList.add('dragging-file'); });
    drop.addEventListener('dragleave', () => drop.classList.remove('dragging-file'));
    drop.addEventListener('drop', (event) => { event.preventDefault(); drop.classList.remove('dragging-file'); this.masterIntent = false; const files = [...((event as DragEvent).dataTransfer?.files ?? [])]; if (files.length) this.beginImport(files); });
    required<HTMLSelectElement>('#label-select').addEventListener('change', (event) => this.setLabelComposition((event.target as HTMLSelectElement).value));
    required<HTMLInputElement>('#labels-visible-toggle').addEventListener('change', (event) => this.setLabelComposition((event.target as HTMLInputElement).checked ? 'entity' : 'none'));
    document.querySelectorAll<HTMLButtonElement>('[data-view-level]').forEach((button) => button.addEventListener('click', () => {
      const level = button.dataset.viewLevel as EntityViewLevel;
      this.setEntityViewLevel(level === 'fragment' && this.store.getState().entityViewLevel === 'fragment' ? 'suerte' : level);
    }));
    required<HTMLSelectElement>('#basemap-select').addEventListener('change', (event) => this.store.update({ basemap: (event.target as HTMLSelectElement).value as BasemapId }));
    document.querySelectorAll<HTMLInputElement>('[data-hybrid-reference]').forEach((input) => input.addEventListener('change', () => {
      const key = input.dataset.hybridReference as keyof HybridReferenceVisibility;
      this.store.update({ hybridReference: { ...this.store.getState().hybridReference, [key]: input.checked } });
    }));
    document.querySelectorAll<HTMLInputElement>('[data-casur-hydric]').forEach((input) => input.addEventListener('change', () => {
      const key = input.dataset.casurHydric as keyof CasurHydricVisibility;
      this.store.update({ casurHydricVisibility: { ...this.store.getState().casurHydricVisibility, [key]: input.checked } });
    }));
    const hydricInput = required<HTMLInputElement>('#casur-hydric-file-input');
    document.querySelectorAll<HTMLButtonElement>('[data-casur-hydric-import]').forEach((button) => button.addEventListener('click', () => hydricInput.click()));
    hydricInput.addEventListener('change', () => { const file = hydricInput.files?.[0]; hydricInput.value = ''; if (file) void this.prepareCasurHydricReference(file); });
    required('#confirm-casur-hydric-import').addEventListener('click', () => void this.commitCasurHydricReference());
    required('#cancel-casur-hydric-import').addEventListener('click', () => { this.pendingCasurHydricReference = null; required<HTMLDialogElement>('#casur-hydric-dialog').close(); });
    required('#zoom-selection').addEventListener('click', () => this.zoomToCurrentSelection());
    required('#zoom-visible').addEventListener('click', () => this.zoomToVisible());
    required('#zoom-layer').addEventListener('click', () => this.mapController.zoomToLayer());
    document.querySelectorAll<HTMLButtonElement>('[data-map-tool]').forEach((button) => button.addEventListener('click', () => this.activateTool(button.dataset.mapTool as MapTool)));
    required('#clear-selection').addEventListener('click', () => this.selectionController.clear());
    required('#clear-measurement').addEventListener('click', () => this.measurementController.clear());
    required('#move-labels').addEventListener('click', () => this.activateTool('move-labels'));
    required('#reset-label').addEventListener('click', () => this.labelPositionController.resetActive());
    required('#reset-selected-labels').addEventListener('click', () => this.labelPositionController.resetSelected());
    required('#reset-all-labels').addEventListener('click', () => this.labelPositionController.resetAll());
    required('#subarea-measure-only').addEventListener('click', () => {
      const preview = this.pendingSubareaPreview; if (!preview) return;
      required('#measurement-status').textContent = `Área efectiva ${preview.effectiveAreaM2.toLocaleString('es-NI', { maximumFractionDigits: 1 })} m² · ${(preview.effectiveAreaM2 / 10_000).toLocaleString('es-NI', { maximumFractionDigits: 3 })} ha · ${preview.coveragePercent.toLocaleString('es-NI', { maximumFractionDigits: 2 })}%`;
      this.pendingSubareaPreview = null; required<HTMLDialogElement>('#subarea-preview-dialog').close(); required('#partial-area-status').textContent = 'Medición parcial completada · geometría temporal descartada';
    });
    required('#subarea-create').addEventListener('click', () => this.saveSubarea(false));
    required('#subarea-create-assign').addEventListener('click', () => this.saveSubarea(true));
    required('#subarea-cancel').addEventListener('click', () => required<HTMLDialogElement>('#subarea-preview-dialog').close());
    required<HTMLDialogElement>('#subarea-preview-dialog').addEventListener('close', () => { this.pendingSubareaPreview = null; this.mapController.setPartialAreaPreview(null); });
    required('#shapefile-crs-list').addEventListener('change', () => { required<HTMLButtonElement>('#apply-shapefile-crs').disabled = !readCrsChoices(required('#shapefile-crs-list')).complete; });
    required('#apply-shapefile-crs').addEventListener('click', () => {
      // No CRS is ever preselected: continue only once every dataset has an explicit choice.
      const choice = readCrsChoices(required('#shapefile-crs-list')); if (!choice.complete) return;
      required<HTMLDialogElement>('#shapefile-crs-dialog').close();
      const hydricFile = this.pendingCasurHydricFile; this.pendingCasurHydricFile = null;
      if (hydricFile) { void this.prepareCasurHydricReference(hydricFile, Object.values(choice.values)[0]); return; }
      const files = this.pendingShapefileFiles; const crsByDataset = { ...this.pendingShapefileCrs, ...choice.values };
      this.pendingShapefileFiles = []; this.pendingShapefileCrs = {};
      if (files.length) void this.importFiles(files, crsByDataset);
    });
    required('#cancel-shapefile-crs').addEventListener('click', () => {
      this.pendingShapefileFiles = []; this.pendingShapefileCrs = {};
      this.pendingCasurHydricFile = null;
      this.clearPendingMasterSource();
      required<HTMLDialogElement>('#shapefile-crs-dialog').close();
    });
    required('#apply-geopackage-layers').addEventListener('click', () => {
      const selected = [...document.querySelectorAll<HTMLInputElement>('#geopackage-layer-list input[type="radio"]:checked')].map(({ value }) => value);
      if (!selected.length) { required('#geopackage-selection-message').textContent = 'Selecciona al menos una capa.'; return; }
      const file = this.pendingGeoPackageFile;
      this.pendingGeoPackageFile = null;
      required<HTMLDialogElement>('#geopackage-layer-dialog').close();
      if (file) void this.loadGeoPackageLayers(file, selected);
    });
    required('#cancel-geopackage-layers').addEventListener('click', () => {
      this.pendingGeoPackageFile = null;
      this.clearPendingMasterSource();
      required<HTMLDialogElement>('#geopackage-layer-dialog').close();
    });
    required('#apply-import-role').addEventListener('click', () => void this.applyPendingImport());
    document.querySelectorAll<HTMLInputElement>('input[name="import-role"]').forEach((radio) => radio.addEventListener('change', () => {
      required<HTMLInputElement>('#remember-master-source').disabled = radio.value === 'additional' && radio.checked;
      this.renderImportSummary();
    }));
    required('#cancel-import-role').addEventListener('click', () => {
      this.pendingImportedLayers = []; this.pendingImportSummaries = new Map(); this.pendingImportNotices = [];
      this.clearPendingMasterSource();
      required<HTMLDialogElement>('#import-role-dialog').close();
    });
  }

  /** "Crear área restante": no drawing tool; the complementary geometry opens in the same preview dialog. */
  private showRemainingSubareaPreview(parent: SubareaParent): void {
    const state = this.store.getState(); if (!state.model) return;
    try { this.showSubareaPreview(createRemainingSubareaPreview(state.model, parent, state.subareas)); } catch (error) {
      this.store.update({ error: error instanceof Error ? error.message : 'No fue posible calcular el área restante.' }, { markDirty: false });
    }
  }

  private showSubareaPreview(preview: SubareaPreview): void {
    this.pendingSubareaPreview = preview;
    this.mapController.setPartialAreaPreview(preview.geometry);
    const state = this.store.getState(); const overlap = overlapAreaM2(preview, state.subareas);
    const unit = (value: number, digits = 2) => value.toLocaleString('es-NI', { maximumFractionDigits: digits });
    const remaining = preview.remaining;
    required('#subarea-preview-dialog .dialog-header h2').textContent = remaining ? 'Crear área restante' : 'Previsualizar intersección';
    required<HTMLButtonElement>('#subarea-measure-only').hidden = Boolean(remaining);
    const parentCells = `<div><b>${esc(preview.fincaName)}</b><span>Finca · ${esc(preview.codfinca)}</span></div><div><b>S${esc(preview.codSuerte)}${preview.tablon ? ` · T${esc(preview.tablon)}` : ''}</b><span>Padre ${preview.parent.level}</span></div><div><b>${unit(preview.parentGeometryAreaM2 / 10_000, 4)} ha</b><span>Área geométrica padre</span></div><div><b>${preview.parentDeclaredAreaHa === undefined ? '—' : `${unit(preview.parentDeclaredAreaHa, 4)} ha`}</b><span>Área declarada padre</span></div>`;
    const parts = `<div><b>${preview.resultPartCount}</b><span>Parte(s) ${preview.resultPartCount > 1 ? '· MultiPolygon' : '· Polygon'}</span></div><div><b>${unit(preview.effectiveAreaM2 / 7_042.25, 4)} mz</b><span>Área efectiva</span></div>`;
    required('#subarea-preview-metrics').innerHTML = remaining
      ? `<div class="subarea-preview-grid">${parentCells}<div><b>${unit(remaining.coveredBeforeAreaM2 / 10_000, 4)} ha</b><span>Área ya cubierta · ${remaining.existingCount} subárea(s)</span></div><div><b>${unit(preview.effectiveAreaM2, 1)} m² · ${unit(preview.effectiveAreaM2 / 10_000, 4)} ha</b><span>Área restante a crear</span></div><div><b>${unit(remaining.coverageBeforePercent)}%</b><span>Cobertura actual</span></div><div><b>${unit(remaining.coverageBeforePercent + preview.coveragePercent)}%</b><span>Cobertura después de crear</span></div>${parts}</div>`
      : `<div class="subarea-preview-grid">${parentCells}<div><b>${unit(preview.drawnAreaM2, 1)} m²</b><span>Dibujo antes de recorte</span></div><div><b>${unit(preview.effectiveAreaM2, 1)} m² · ${unit(preview.effectiveAreaM2 / 10_000, 4)} ha</b><span>Intersección efectiva</span></div><div><b>${unit(preview.coveragePercent)}%</b><span>Cobertura geométrica</span></div><div><b>${unit(preview.discardedAreaM2, 1)} m²</b><span>Descartado fuera del límite</span></div>${parts}</div>`;
    required<HTMLInputElement>('#subarea-name').value = remaining ? 'Área restante' : `Área parcial S${preview.codSuerte}${preview.tablon ? ` T${preview.tablon}` : ''}`;
    const fields = state.operationalFields.filter(({ descriptor }) => descriptor.source === 'project' && descriptor.level === 'subarea');
    required('#subarea-assignment-fields').innerHTML = fields.length ? fields.map(({ descriptor }, index) => `<div class="subarea-assignment-row"><input type="checkbox" data-subarea-field-check="${esc(descriptor.id)}"/><label>${esc(descriptor.name)} · ${esc(descriptor.type)}</label><input data-subarea-field-value="${esc(descriptor.id)}" placeholder="Valor"${descriptor.suggestedValues?.length ? ` list="subarea-values-${index}"` : ''} />${descriptor.suggestedValues?.length ? `<datalist id="subarea-values-${index}">${descriptor.suggestedValues.map((value) => `<option value="${esc(value)}"></option>`).join('')}</datalist>` : ''}</div>`).join('') : '<small>Crea campos de proyecto con nivel Área parcial para asignarlos aquí.</small>';
    required('#subarea-validation').textContent = overlap > 0.01 ? `Bloqueado: ${unit(overlap, 2)} m² se superponen con otra subárea del mismo padre.` : 'La geometría se guardará separada del Maestro CASUR.';
    required<HTMLButtonElement>('#subarea-create').disabled = overlap > 0.01;
    required<HTMLButtonElement>('#subarea-create-assign').disabled = overlap > 0.01;
    required<HTMLDialogElement>('#subarea-preview-dialog').showModal();
    required('#partial-area-status').textContent = remaining ? 'Área restante calculada · revisa superficie y cobertura' : 'Intersección lista · revisa área, cobertura y descarte';
  }

  private saveSubarea(assign: boolean): void {
    const preview = this.pendingSubareaPreview; if (!preview) return; const state = this.store.getState();
    try {
      const subarea = createOperationalSubarea(preview, required<HTMLInputElement>('#subarea-name').value, state.subareas);
      let operationalFields = state.operationalFields;
      const checked = assign ? [...document.querySelectorAll<HTMLInputElement>('[data-subarea-field-check]:checked')] : [];
      if (assign && !checked.length) throw new Error('Selecciona al menos un campo de área parcial para “Crear + asignar atributos”.');
      checked.forEach((input) => {
        const field = operationalFields.find(({ descriptor }) => descriptor.id === input.dataset.subareaFieldCheck);
        const valueInput = field ? document.querySelector<HTMLInputElement>(`[data-subarea-field-value="${CSS.escape(field.descriptor.id)}"]`) : null;
        if (!field || !valueInput) return;
        const value = parseFieldValue(valueInput.value, field.descriptor.type);
        operationalFields = applyAssignment(operationalFields, { entityIds: [subarea.id], entityCount: 1, areaHa: subarea.areaHa, field, value });
      });
      const entry: SelectionEntry = { source: 'generic', layerId: SUBAREA_LAYER_ID, kind: 'feature', id: subarea.id };
      this.pendingSubareaPreview = null; required<HTMLDialogElement>('#subarea-preview-dialog').close();
      this.store.update({ subareas: [...state.subareas, subarea], operationalFields, activeLayerId: SUBAREA_LAYER_ID, selectionSet: { layerId: SUBAREA_LAYER_ID, entries: [entry], focus: entry }, genericSelection: { layerId: SUBAREA_LAYER_ID, featureId: subarea.id }, selection: null, activeTool: 'select', error: null });
      required('#partial-area-status').textContent = `Subárea guardada · ${subarea.areaHa.toLocaleString('es-NI', { maximumFractionDigits: 3 })} ha`;
    } catch (error) { required('#subarea-validation').textContent = error instanceof Error ? error.message : 'No fue posible guardar la subárea.'; }
  }

  private async restoreCasurHydricReference(fromProjectOpen: boolean): Promise<void> {
    this.store.update({ casurHydricLoading: true, casurHydricError: null }, { markDirty: false });
    try {
      const reference = await this.casurHydricRepository.load();
      this.store.update({
        casurHydricReference: reference,
        casurHydricSelection: null,
        casurHydricLoading: false,
        casurHydricError: !reference && fromProjectOpen ? 'Referencia CASUR no disponible en este dispositivo' : null,
      }, { markDirty: false });
    } catch (error) {
      this.store.update({ casurHydricReference: null, casurHydricSelection: null, casurHydricLoading: false, casurHydricError: error instanceof Error ? error.message : 'No fue posible leer la Referencia CASUR local.' }, { markDirty: false });
    }
  }

  private async prepareCasurHydricReference(file: File, fallbackCrs?: ManualShapefileCrs): Promise<void> {
    this.store.update({ casurHydricLoading: true, casurHydricError: null }, { markDirty: false });
    await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
    try {
      const reference = await importCasurHydricReference(file, fallbackCrs);
      this.pendingCasurHydricReference = reference;
      const metadata = reference.metadata;
      required('#casur-hydric-import-summary').textContent = `Archivo: ${metadata.sourceFileName}\nCanales: ${metadata.channelCount.toLocaleString('es-NI')}\nDrenajes: ${metadata.drainageCount.toLocaleString('es-NI')}\nCRS: ${metadata.sourceCrs.join(' · ')}\nAdvertencias: ${metadata.warnings.length}`;
      required('#casur-hydric-import-warnings').textContent = metadata.warnings.length ? metadata.warnings.join('\n') : 'Validación completa sin advertencias.';
      required<HTMLDialogElement>('#casur-hydric-dialog').showModal();
      this.store.update({ casurHydricLoading: false }, { markDirty: false });
    } catch (error) {
      if (error instanceof ShapefileCrsRequiredError) {
        this.pendingCasurHydricFile = file;
        this.showShapefileCrsDialog([{ key: '*', name: error.datasetNames.join(', '), reason: error.reason }], `${error.message} La Referencia CASUR no se guardará hasta que confirmes el CRS.`);
        this.store.update({ casurHydricLoading: false }, { markDirty: false });
        return;
      }
      this.store.update({ casurHydricLoading: false, casurHydricError: error instanceof Error ? error.message : 'No fue posible validar la Referencia CASUR.' }, { markDirty: false });
    }
  }

  private async commitCasurHydricReference(): Promise<void> {
    const next = this.pendingCasurHydricReference; if (!next) return;
    const previous = this.store.getState().casurHydricReference;
    this.store.update({ casurHydricLoading: true, casurHydricError: null }, { markDirty: false });
    try {
      await this.casurHydricRepository.save(next);
      this.pendingCasurHydricReference = null;
      required<HTMLDialogElement>('#casur-hydric-dialog').close();
      this.store.update({ casurHydricReference: next, casurHydricSelection: null, casurHydricLoading: false, casurHydricError: null }, { markDirty: false });
    } catch (error) {
      this.store.update({ casurHydricReference: previous, casurHydricLoading: false, casurHydricError: error instanceof Error ? error.message : 'No fue posible guardar la Referencia CASUR; se conservó la versión anterior.' }, { markDirty: false });
    }
  }

  private beginImport(files: readonly File[], masterSource?: { handle: MasterSourceFileHandle; file: File }): void {
    this.sourceOperation += 1; // supersedes a recovery of the remembered Maestro still under way
    this.pendingMasterSourceHandle = masterSource?.handle ?? null;
    this.pendingMasterSourceFile = masterSource?.file ?? null;
    void this.importFiles(files);
  }

  private handleGeometryAction(action: string): void {
    const state = this.store.getState();
    if (action === 'begin' || action === 'begin-fragment') {
      if (!state.sourceModel || !state.model) return;
      const target = geometryEditTargetForSelection(state.model, state.selection);
      if (!target) return;
      if (action === 'begin-fragment' && target.activeFragmentCount < 2) { this.store.update({ error: 'No se puede excluir el último fragmento de la entidad.' }, { markDirty: false }); return; }
      const session = {
        ...beginGeometryEdit(state.geometryEdits, target.entityKind, target.entityId),
        selectedFragmentId: action === 'begin-fragment' ? target.selectedFragmentId : null,
      };
      this.store.update({ geometryEditSession: session, activeTool: 'select', error: null }, { markDirty: false });
      if (action === 'begin-fragment' && session.selectedFragmentId) this.showFragmentExclusionPreview();
      return;
    }
    if (action === 'difference') { this.openDifferenceInputDialog(); return; }
    const session = state.geometryEditSession;
    if (!session || !state.sourceModel || !state.model) return;
    if (action === 'exclude') { this.showFragmentExclusionPreview(); return; }
    if (action === 'cancel') { this.cancelGeometryEdit(); return; }
    if (action === 'apply') { this.commitGeometryEdit(); return; }
    const next = action === 'undo' ? undoGeometryEdit(session) : action === 'redo' ? redoGeometryEdit(session) : null;
    if (!next || next === session) return;
    const model = deriveActiveLayerModel(state.sourceModel, next.history);
    const restoredFragmentId = action === 'undo' ? session.history.commands[session.history.cursor - 1]?.fragmentId : null;
    const presentedSession = restoredFragmentId && model.fragmentsById.has(restoredFragmentId)
      ? { ...next, selectedFragmentId: restoredFragmentId }
      : next;
    this.store.update({ geometryEditSession: presentedSession, model, layers: withActiveMasterModel(state.layers, model), error: null }, { markDirty: false });
  }

  private openDifferenceInputDialog(): void {
    const state = this.store.getState();
    const session = state.geometryEditSession;
    const selectionTarget = state.model ? geometryEditTargetForSelection(state.model, state.selection) : null;
    const target = session?.selectedFragmentId
      ? state.model?.fragmentsById.get(session.selectedFragmentId)
      : state.model && selectionTarget?.selectedFragmentId
        ? state.model.fragmentsById.get(selectionTarget.selectedFragmentId)
        : null;
    if (!state.sourceModel || !state.model || !target) {
      this.store.update({ error: 'Selecciona exactamente un fragmento físico para quitar área.' }, { markDirty: false });
      return;
    }
    required<HTMLDialogElement>('#difference-input-dialog').showModal();
  }

  private beginDifferenceDrawing(mode: 'free' | 'boundary'): void {
    const state = this.store.getState();
    if (!state.sourceModel || !state.model) return;
    let session = state.geometryEditSession;
    const startsSession = !session;
    if (!session) {
      const target = geometryEditTargetForSelection(state.model, state.selection);
      if (!target?.selectedFragmentId) {
        this.store.update({ error: 'Selecciona exactamente un fragmento físico para quitar área.' }, { markDirty: false });
        return;
      }
      session = { ...beginGeometryEdit(state.geometryEdits, target.entityKind, target.entityId), selectedFragmentId: target.selectedFragmentId };
    }
    if (!session.selectedFragmentId || !state.model.fragmentsById.has(session.selectedFragmentId)) {
      this.store.update({ error: 'Selecciona un fragmento activo de la entidad en edición.' }, { markDirty: false });
      return;
    }
    this.differenceStartedSession = startsSession;
    this.differenceDrawMode = mode;
    this.boundaryCutStarted = false;
    this.clearBoundaryCutEndpoints();
    this.store.update({ geometryEditSession: session, activeTool: 'navigate', error: null }, { markDirty: false });
    const targetGeometry = state.model.fragmentsById.get(session.selectedFragmentId)?.feature.getGeometry();
    if (!targetGeometry) {
      this.store.update({ error: 'El fragmento activo no contiene una geometría válida.' }, { markDirty: false });
      return;
    }
    const completeFreeCut = (geometry: Geometry): void => {
      const current = this.store.getState();
      if (!current.geometryEditSession || !current.sourceModel || !current.model) return;
      try {
        const preview = previewDifferenceFragment(current.geometryEditSession, current.sourceModel, current.model, geometry);
        this.pendingDifferencePreview = preview;
        this.mapController.setDifferencePreview(preview.intersectionGeometry, preview.resultGeometry);
        this.showDifferencePreview(preview);
        this.store.update({ activeTool: 'select', error: null }, { markDirty: false });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'No fue posible calcular el recorte Difference.';
        required('#drawing-status').textContent = message;
        this.store.update({ activeTool: 'select', error: message }, { markDirty: false });
      }
    };
    if (mode === 'free') {
      this.drawingController.capturePolygon('draw-polygon', completeFreeCut, {
        purpose: 'difference',
        cancel: () => this.cancelDifferenceCapture(),
        targetGeometry,
        assistStateChanged: (assistState) => this.renderDifferenceDrawingAssist(assistState),
      });
      return;
    }
    this.drawingController.captureBoundaryCut((line) => {
      this.differenceDrawMode = null;
      try {
        this.pendingBoundaryCutPlan = buildBoundaryCutPlan(targetGeometry, line);
        this.showBoundaryCutCandidates(this.pendingBoundaryCutPlan);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'No fue posible construir el Corte al borde.';
        required('#drawing-status').textContent = message;
        this.store.update({ activeTool: 'select', error: message }, { markDirty: false });
        this.clearBoundaryCutEndpoints();
        if (this.differenceStartedSession) this.cancelDifferenceCapture();
      }
    }, {
      cancel: () => this.cancelDifferenceCapture(),
      targetGeometry,
      assistStateChanged: (assistState, started) => {
        this.boundaryCutStarted = started;
        this.updateBoundaryCutEndpoints(assistState, started);
        this.renderDifferenceDrawingAssist(assistState);
      },
    });
  }

  private syncDifferenceDrawingTarget(state: ReturnType<AppStore['getState']>): void {
    const fragmentId = state.geometryEditSession?.selectedFragmentId;
    const geometry = fragmentId ? state.model?.fragmentsById.get(fragmentId)?.feature.getGeometry() : null;
    if (geometry) this.drawingController.updateDifferenceTarget(geometry);
  }

  private renderDifferenceDrawingAssist(state: DifferenceDrawAssistState): void {
    const controls = required<HTMLElement>('#difference-draw-assist');
    required<HTMLElement>('.app-shell').classList.toggle('difference-drawing', state.active);
    controls.hidden = !state.active;
    controls.classList.toggle('is-snapped', state.snapped);
    const snap = required<HTMLButtonElement>('#difference-snap-toggle');
    const trace = required<HTMLButtonElement>('#difference-trace-toggle');
    const boundaryMode = this.differenceDrawMode === 'boundary';
    snap.disabled = boundaryMode;
    trace.hidden = boundaryMode;
    snap.setAttribute('aria-pressed', String(state.snapEnabled));
    trace.setAttribute('aria-pressed', String(state.traceEnabled));
    const feedback = required('#difference-snap-feedback');
    feedback.textContent = boundaryMode
      ? state.snapped
        ? this.boundaryCutStarted ? 'Final ajustado al borde' : 'Inicio ajustado al borde'
        : this.boundaryCutStarted ? 'Ajusta el final al borde' : 'Ajusta el inicio al borde'
      : state.snapped
      ? 'Ajustado al borde'
      : state.traceEnabled
        ? `Seguir borde activo · ${DIFFERENCE_DRAW_ASSISTANCE.snapPixelTolerance} px`
        : state.snapEnabled
          ? `Snap listo · ${DIFFERENCE_DRAW_ASSISTANCE.snapPixelTolerance} px`
          : 'Snap desactivado';
  }

  private updateBoundaryCutEndpoints(state: DifferenceDrawAssistState, started: boolean): void {
    if (!state.active || !started) return;
    if (!state.snapped || !state.snappedCoordinate) {
      this.boundaryCutEndCoordinate = null;
    } else if (!this.boundaryCutStartCoordinate) {
      this.boundaryCutStartCoordinate = [...state.snappedCoordinate];
    } else if (this.boundaryCutStartCoordinate.some((value, index) => value !== state.snappedCoordinate?.[index])) {
      this.boundaryCutEndCoordinate = [...state.snappedCoordinate];
    } else {
      this.boundaryCutEndCoordinate = null;
    }
    this.mapController.setBoundaryCutEndpoints(this.boundaryCutStartCoordinate, this.boundaryCutEndCoordinate);
  }

  private clearBoundaryCutEndpoints(): void {
    this.boundaryCutStartCoordinate = null;
    this.boundaryCutEndCoordinate = null;
    this.mapController.setBoundaryCutEndpoints(null, null);
  }

  private showBoundaryCutCandidates(plan: BoundaryCutPlan): void {
    this.mapController.setBoundaryCutCandidates(plan.candidates);
    const choice = required<HTMLElement>('#boundary-cut-choice');
    choice.hidden = false;
    required('#boundary-cut-candidate-a-area').textContent = `${plan.candidateAreaHa[0].toLocaleString('es-NI', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ha`;
    required('#boundary-cut-candidate-b-area').textContent = `${plan.candidateAreaHa[1].toLocaleString('es-NI', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ha`;
    let hovered: number | null = null;
    this.clearBoundaryCutListeners();
    this.boundaryCutEventKeys = [
      this.mapController.map.on('pointermove', (event) => {
        const candidate = this.mapController.boundaryCutCandidateAtPixel(event.pixel);
        if (candidate === hovered) return;
        hovered = candidate;
        this.mapController.setBoundaryCutCandidates(plan.candidates, hovered);
        this.mapController.map.getTargetElement().style.cursor = hovered === null ? '' : 'pointer';
      }),
      this.mapController.map.on('singleclick', (event) => {
        const candidate = this.mapController.boundaryCutCandidateAtPixel(event.pixel);
        if (candidate !== null) this.selectBoundaryCutCandidate(candidate);
      }),
    ];
    required('#drawing-status').textContent = 'Elige en el mapa el lado que deseas quitar.';
  }

  private selectBoundaryCutCandidate(index: number): void {
    const plan = this.pendingBoundaryCutPlan;
    const current = this.store.getState();
    if (!plan || !current.geometryEditSession || !current.sourceModel || !current.model || (index !== 0 && index !== 1)) return;
    try {
      const preview = previewDifferenceFragment(current.geometryEditSession, current.sourceModel, current.model, plan.candidates[index]!);
      this.pendingDifferencePreview = preview;
      this.clearBoundaryCutChoice();
      this.mapController.setDifferencePreview(preview.intersectionGeometry, preview.resultGeometry);
      this.showDifferencePreview(preview);
      this.store.update({ activeTool: 'select', error: null }, { markDirty: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No fue posible calcular el lado elegido.';
      required('#drawing-status').textContent = message;
      this.store.update({ error: message }, { markDirty: false });
    }
  }

  private cancelBoundaryCutChoice(): void {
    this.clearBoundaryCutChoice();
    this.cancelDifferenceCapture();
  }

  private clearBoundaryCutChoice(): void {
    this.pendingBoundaryCutPlan = null;
    this.mapController.setBoundaryCutCandidates([]);
    required<HTMLElement>('#boundary-cut-choice').hidden = true;
    this.clearBoundaryCutListeners();
  }

  private clearBoundaryCutListeners(): void {
    unByKey(this.boundaryCutEventKeys);
    this.boundaryCutEventKeys = [];
    this.mapController.map.getTargetElement().style.cursor = '';
  }

  private showDifferencePreview(preview: DifferenceFragmentPreview): void {
    const state = this.store.getState(); const session = state.geometryEditSession;
    if (!session || !state.model) return;
    const label = session.entityKind === 'suerte'
      ? `Suerte ${state.model.suertes.get(session.entityId)?.label ?? ''}`
      : `Tablón ${state.model.tablones.get(session.entityId)?.code ?? ''}`;
    required('#difference-fragment-entity').textContent = `${label} · fragmento ${preview.fragmentId}`;
    required('#difference-fragment-metrics').innerHTML = [
      ['Área original del fragmento', preview.originalAreaHa],
      ['Área activa actual', preview.activeAreaHa],
      ['Área intersectada / retirada', preview.removedAreaHa],
      ['Área resultante', preview.resultAreaHa],
    ].map(([name, value]) => `<div><b>${Number(value).toLocaleString('es-NI', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ha</b><span>${name}</span></div>`).join('');
    required('#difference-fragment-validation').textContent = 'Magenta: área retirada. Cian: resultado. La geometría y los atributos fuente permanecerán intactos.';
    required<HTMLDialogElement>('#difference-fragment-dialog').showModal();
  }

  private applyDifferencePreview(): void {
    const state = this.store.getState(); const session = state.geometryEditSession; const preview = this.pendingDifferencePreview;
    if (!session || !state.sourceModel || !preview) return;
    try {
      const next = applyDifferenceFragment(session, preview);
      const model = deriveActiveLayerModel(state.sourceModel, next.history);
      this.pendingDifferencePreview = null;
      this.differenceStartedSession = false;
      this.differenceDrawMode = null;
      this.clearBoundaryCutEndpoints();
      this.clearBoundaryCutChoice();
      this.mapController.setDifferencePreview(null, null);
      required<HTMLDialogElement>('#difference-fragment-dialog').close();
      this.store.update({ geometryEditSession: next, model, layers: withActiveMasterModel(state.layers, model), activeTool: 'select', error: null }, { markDirty: false });
    } catch (error) {
      required('#difference-fragment-validation').textContent = error instanceof Error ? error.message : 'No fue posible aplicar el recorte.';
    }
  }

  private cancelDifferenceCapture(): void {
    this.clearBoundaryCutEndpoints();
    this.clearBoundaryCutChoice();
    this.differenceDrawMode = null;
    const state = this.store.getState();
    if (!this.differenceStartedSession) {
      this.store.update({ activeTool: 'select', error: null }, { markDirty: false });
      return;
    }
    if (!state.sourceModel) return;
    const model = deriveActiveLayerModel(state.sourceModel, state.geometryEdits);
    this.differenceStartedSession = false;
    this.store.update({ geometryEditSession: null, model, layers: withActiveMasterModel(state.layers, model), activeTool: 'select', error: null }, { markDirty: false });
  }

  private cancelDifferencePreview(): void {
    this.pendingDifferencePreview = null;
    this.clearBoundaryCutEndpoints();
    this.mapController.setDifferencePreview(null, null);
    this.clearBoundaryCutChoice();
    required<HTMLDialogElement>('#difference-fragment-dialog').close();
    if (this.differenceStartedSession) this.cancelDifferenceCapture();
  }

  private showFragmentExclusionPreview(): void {
    const state = this.store.getState(); const session = state.geometryEditSession;
    if (!session || !state.sourceModel || !state.model || !session.selectedFragmentId) return;
    const metrics = geometryEntityMetrics(state.sourceModel, state.model, session.entityKind, session.entityId);
    const selectedArea = fragmentAreaHa(state.model, session.selectedFragmentId);
    const blocked = metrics.activeFragmentCount <= 1;
    const entity = session.entityKind === 'suerte' ? state.model.suertes.get(session.entityId) : state.model.tablones.get(session.entityId);
    const label = session.entityKind === 'suerte' ? `Suerte ${state.model.suertes.get(session.entityId)?.label ?? ''}` : `Tablón ${state.model.tablones.get(session.entityId)?.code ?? ''}`;
    required('#exclude-fragment-entity').textContent = `${label} · ${entity?.fragmentIds.length ?? 0} fragmento(s) activos`;
    required('#exclude-fragment-metrics').innerHTML = [
      ['Área original', metrics.originalAreaHa],
      ['Área activa actual', metrics.activeAreaHa],
      ['Fragmento a excluir', selectedArea],
      ['Área resultante', Math.max(0, metrics.activeAreaHa - selectedArea)],
    ].map(([name, value]) => `<div><b>${Number(value).toLocaleString('es-NI', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha</b><span>${name}</span></div>`).join('');
    required('#exclude-fragment-validation').textContent = blocked ? 'Bloqueado: no se puede excluir el último fragmento activo.' : 'La geometría fuente permanecerá intacta. La exclusión se añadirá al borrador de esta sesión.';
    required<HTMLButtonElement>('#apply-exclude-fragment').disabled = blocked;
    required<HTMLDialogElement>('#exclude-fragment-dialog').showModal();
  }

  private applyFragmentExclusion(): void {
    const state = this.store.getState(); const session = state.geometryEditSession;
    if (!session || !state.sourceModel || !state.model) return;
    try {
      const next = excludeFragment(session, state.sourceModel, state.model);
      const model = deriveActiveLayerModel(state.sourceModel, next.history);
      required<HTMLDialogElement>('#exclude-fragment-dialog').close();
      this.store.update({ geometryEditSession: next, model, layers: withActiveMasterModel(state.layers, model), error: null }, { markDirty: false });
    } catch (error) {
      required('#exclude-fragment-validation').textContent = error instanceof Error ? error.message : 'No fue posible excluir el fragmento.';
    }
  }

  private cancelGeometryEdit(): void {
    const state = this.store.getState(); if (!state.sourceModel || !state.geometryEditSession) return;
    const model = deriveActiveLayerModel(state.sourceModel, state.geometryEdits);
    this.differenceStartedSession = false;
    this.drawingController.cancelSketch();
    required<HTMLDialogElement>('#exclude-fragment-dialog').close();
    if (required<HTMLDialogElement>('#difference-fragment-dialog').open) required<HTMLDialogElement>('#difference-fragment-dialog').close();
    this.pendingDifferencePreview = null;
    this.differenceDrawMode = null;
    this.clearBoundaryCutEndpoints();
    this.clearBoundaryCutChoice();
    this.mapController.setDifferencePreview(null, null);
    this.store.update({ geometryEditSession: null, model, layers: withActiveMasterModel(state.layers, model), error: null }, { markDirty: false });
  }

  /**
   * V1.9.0 E5 · completes a Deshacer / Rehacer patch: restored geometry edits rebuild the active Maestro model (the
   * same derivation as the editing session), restored layers get the active model back (the history records them
   * without it), and a selection or active layer that points at something the step removed is cleared. The Maestro's
   * source geometry is never modified.
   */
  private completeHistoryPatch(patch: Partial<AppState>, state: AppState): Partial<AppState> {
    const next: { -readonly [K in keyof AppState]?: AppState[K] } = { ...patch };
    if (('geometryEdits' in patch || 'sourceModel' in patch) && !('model' in patch)) {
      const source = patch.sourceModel ?? state.sourceModel;
      if (source) { const model = deriveActiveLayerModel(source, patch.geometryEdits ?? state.geometryEdits); next.model = model; next.layers = withActiveMasterModel(patch.layers ?? state.layers, model); }
    }
    const model = 'model' in next ? next.model : state.model;
    if (patch.layers && next.layers === patch.layers && model) next.layers = withActiveMasterModel(patch.layers, model);
    if (next.layers) {
      const exists = (layerId: string, id: string) => next.layers!.find((layer) => layer.id === layerId)?.featuresById.has(id) ?? false;
      if (state.genericSelection && !exists(state.genericSelection.layerId, state.genericSelection.featureId)) next.genericSelection = null;
      const entries = state.selectionSet.entries.filter((entry) => entry.source !== 'generic' || exists(entry.layerId, entry.id));
      if (entries.length !== state.selectionSet.entries.length) next.selectionSet = { ...state.selectionSet, entries, focus: state.selectionSet.focus && entries.includes(state.selectionSet.focus) ? state.selectionSet.focus : entries.at(-1) ?? null };
      if (state.activeLayerId && !next.layers.some((layer) => layer.id === state.activeLayerId)) next.activeLayerId = next.layers.at(-1)?.id ?? null;
    }
    return next;
  }

  private commitGeometryEdit(): void {
    const state = this.store.getState(); const session = state.geometryEditSession;
    if (!session) return;
    const changed = !sameGeometryEditHistory(state.geometryEdits, session.history);
    this.differenceStartedSession = false;
    this.drawingController.cancelSketch();
    this.pendingDifferencePreview = null;
    this.differenceDrawMode = null;
    this.clearBoundaryCutEndpoints();
    this.clearBoundaryCutChoice();
    this.mapController.setDifferencePreview(null, null);
    if (required<HTMLDialogElement>('#difference-fragment-dialog').open) required<HTMLDialogElement>('#difference-fragment-dialog').close();
    this.store.update({ geometryEdits: session.history, geometryEditSession: null, error: null }, { markDirty: changed });
  }

  private clearPendingMasterSource(): void {
    this.pendingMasterSourceHandle = null;
    this.pendingMasterSourceFile = null;
    this.pendingMasterSourceLayer = null;
    this.masterIntent = false;
  }

  /**
   * Quick Maestro actions. They reuse the remembered source (permission asked only on this click), the file picker that
   * remembers it, or the plain import input where the browser has no picker; the import dialog always asks which file
   * becomes the Maestro. Replacing a loaded Maestro is confirmed first, mentioning unsaved changes.
   */
  private async quickMaster(action: 'load' | 'change' | 'reload'): Promise<void> {
    const state = this.store.getState();
    const usable = this.rememberedMasterSource && this.masterSourceAvailability !== 'unavailable' && this.masterSourceAvailability !== 'denied';
    if (action === 'reload' || (action === 'load' && !state.model && usable)) { await this.loadRememberedMasterSource(); return; }
    if (state.model && !window.confirm(`${state.projectDirty ? 'Hay cambios sin guardar en el proyecto. ' : ''}Cambiar el Maestro CASUR reemplaza el actual: se reinician la selección, los filtros, la clasificación, los campos de proyecto, las áreas parciales y las selecciones guardadas. Las capas externas y de planificación se conservan. ¿Elegir otro archivo?`)) return;
    if (supportsMasterSourcePicker(window as unknown as MasterSourceBrowser)) { await this.chooseMasterSource(); return; }
    this.masterIntent = true;
    required<HTMLInputElement>('#import-file-input').click();
  }

  private renderMasterQuick(state: ReturnType<AppStore['getState']>): void {
    const element = required<HTMLElement>('#master-quick');
    const source = this.rememberedMasterSource; const availability = this.masterSourceAvailability;
    let html: string;
    if (!state.model) {
      const hint = source
        ? availability === 'unavailable' || availability === 'denied' ? `${esc(source.metadata.name)} ya no está disponible: elige el archivo de nuevo.`
          : `Fuente recordada: ${esc(source.metadata.name)}${availability === 'permission-required' ? ' · el navegador pedirá permiso' : ''}`
        : 'KML, KMZ, GeoJSON, Shapefile (ZIP) o GeoPackage.';
      html = `<button type="button" class="button button-primary master-quick-load" data-master-quick="load">${icon('layers')}Cargar Maestro CASUR</button><small>${hint}</small>`;
    } else {
      const name = state.layers.find((layer) => layer.kind === 'master')?.sourceName ?? state.model.name;
      html = `<span class="master-quick-name" title="Maestro activo · solo lectura">${icon('lock')}<span>${esc(name)}</span></span>${this.masterSourceChanged ? '<button type="button" class="link-button" data-master-quick="reload" title="La fuente cambió desde la última carga">Recargar</button>' : ''}<button type="button" class="link-button" data-master-quick="change">Cambiar Maestro</button>`;
    }
    if (element.dataset.html !== html) { element.innerHTML = html; element.dataset.html = html; }
    element.classList.toggle('loaded', Boolean(state.model));
  }

  private async restoreMasterSource(): Promise<void> {
    const browser = window as unknown as MasterSourceBrowser;
    try {
      const source = await this.masterSourceRepository.load();
      this.rememberedMasterSource = source;
      if (!source) {
        this.masterSourceAvailability = supportsMasterSourcePicker(browser) ? 'none' : 'unsupported';
        this.masterSourceChanged = false;
        this.masterSourceError = null;
      } else {
        const result = await readMasterSource(source, false);
        this.masterSourceAvailability = result.status;
        this.masterSourceChanged = result.status === 'ready' && result.changed;
        this.masterSourceError = result.status === 'unavailable' ? result.error ?? null : null;
        // V1.9.0 E5: recover the last Maestro when GeoStudio opens — only into an empty session, never over a project.
        this.masterRestore = masterRestoreAction(result, { enabled: loadMasterAutoloadPreference(), idle: this.masterRestoreIdle(true) });
      }
    } catch (error) {
      this.masterSourceAvailability = 'unavailable';
      this.masterSourceError = error instanceof Error ? error.message : 'No fue posible leer la fuente recordada.';
    }
    this.renderMasterSource();
    if (this.masterRestore === 'autoload') await this.recoverMaster(false);
  }

  /**
   * Nothing would be replaced by recovering the Maestro: no Maestro, no layer, no import under way and (for the
   * automatic recovery) still the empty project GeoStudio opened with. Panning the empty map does not count.
   */
  private masterRestoreIdle(automatic: boolean): boolean {
    const state = this.store.getState();
    // An import under way: reading (loadingMessage), waiting in its dialog, or its layers not applied yet.
    const importing = Boolean(state.loadingMessage) || this.pendingImportedLayers.length > 0 || Boolean(document.querySelector('dialog[open]'));
    return !state.model && !state.layers.length && !importing && (!automatic || state.project.id === this.initialProjectId);
  }

  /**
   * Loads the remembered Maestro as the Maestro, without the import dialog: at start-up when everything allows it
   * (masterRestoreAction 'autoload'), or from «Recuperar» on the map notice (`fromUser`: the browser may ask for
   * access, and a modified file is loaded because the user asked for it). The file is read and validated like any
   * import; anything that needs a choice (several datasets, coordinate system, GeoPackage table) goes through the
   * normal dialog instead. Something opened meanwhile is never replaced.
   */
  private async recoverMaster(fromUser: boolean): Promise<void> {
    const source = this.rememberedMasterSource;
    if (!source || this.masterRecovering || !this.masterRestoreIdle(!fromUser)) return;
    this.masterRecovering = true;
    const operation = this.sourceOperation;
    // The user started an import meanwhile: that import goes on, this recovery leaves everything as it is.
    const superseded = () => operation !== this.sourceOperation;
    try {
      const result = await readMasterSource(source, fromUser);
      if (superseded()) { this.masterRestore = 'none'; return; }
      this.masterSourceAvailability = result.status;
      this.masterSourceChanged = result.status === 'ready' && result.changed;
      this.masterSourceError = result.status === 'unavailable' ? result.error ?? null : null;
      if (result.status !== 'ready') { this.masterRestore = result.status === 'permission-required' ? 'permission' : 'missing'; return; }
      if (result.changed && !fromUser) { this.masterRestore = 'changed'; return; }
      if (!this.masterRestoreIdle(!fromUser)) { this.masterRestore = 'none'; return; }
      this.store.update({ loadingMessage: `Recuperando el Maestro CASUR · ${source.metadata.name}…`, error: null }, { markDirty: false });
      const layer = await this.readMasterLayer(result.file, source.metadata.layer);
      if (superseded()) { this.masterRestore = 'none'; return; } // its loading message is the new import's now
      this.store.update({ loadingMessage: null }, { markDirty: false });
      if (!layer) {
        this.masterRestore = 'offer';
        if (fromUser) { this.masterRestore = 'none'; this.beginImport([result.file], { handle: source.handle, file: result.file }); }
        return;
      }
      const eligibility = masterEligibility(layer);
      if (!eligibility.eligible) { this.masterRestore = 'missing'; this.masterSourceError = eligibility.reason; return; }
      if (!this.masterRestoreIdle(!fromUser)) { this.masterRestore = 'none'; return; }
      this.applyMasterLayer(layer, { markDirty: fromUser });
      this.masterRestore = 'none';
      const remembered = createRememberedMasterSource(source.handle, result.file, undefined, source.metadata.layer);
      try { await this.masterSourceRepository.save(remembered); this.rememberedMasterSource = remembered; this.masterSourceChanged = false; } catch { /* the Maestro is loaded; its metadata stays as it was */ }
      this.mapController.zoomToLayer(this.store.getState().model?.id);
      this.notify(`Maestro CASUR recuperado · ${source.metadata.name}`);
    } catch (error) {
      if (superseded()) { this.masterRestore = 'none'; return; }
      this.store.update({ loadingMessage: null }, { markDirty: false });
      this.masterRestore = 'missing';
      this.masterSourceError = error instanceof Error ? error.message : 'No fue posible leer el Maestro CASUR recordado.';
    } finally {
      this.masterRecovering = false;
      this.renderMasterSource();
    }
  }

  /** The remembered file as ONE vector layer, or null when its reading needs a choice in the import dialog. */
  private async readMasterLayer(file: File, geoPackageLayer: string | undefined): Promise<VectorLayerModel | null> {
    if (/\.gpkg$/i.test(file.name)) {
      if (!geoPackageLayer) return null;
      const layers = await importGeoPackage(file, [geoPackageLayer]);
      return layers.length === 1 ? layers[0]! : null;
    }
    if (/\.(zip|shp)$/i.test(file.name)) {
      try {
        const inspection = await inspectShapefiles([file]);
        return inspection.datasets.length === 1 ? inspection.datasets[0]!.layer : null;
      } catch (error) { if (error instanceof ShapefileCrsRequiredError) return null; throw error; }
    }
    return importVectorLayer(file);
  }

  /**
   * The empty-map card tells what happened to the remembered Maestro and what can be done — never a silent
   * replacement: recover it, load the modified version, choose another file, or leave it («Ahora no»).
   */
  private masterRestoreHtml(): string {
    const source = this.rememberedMasterSource; const state = this.store.getState();
    const action = source && !state.model && !state.layers.length && !this.masterRecovering ? this.masterRestore : 'none';
    if (!source || action === 'none' || action === 'autoload') return '';
    const name = esc(source.metadata.name);
    const modified = new Date(source.metadata.lastModified).toLocaleString('es-NI', { dateStyle: 'medium', timeStyle: 'short' });
    const [title, detail, primary, secondary] = action === 'changed'
      ? [`<strong>${name}</strong> cambió desde la última carga.`, 'Revisa que sea la versión correcta antes de usarla.', 'Cargar versión actual', 'Elegir otro archivo']
      : action === 'missing'
        ? [`No se pudo abrir <strong>${name}</strong>.`, this.masterSourceError ? esc(this.masterSourceError) : 'Se movió, se eliminó o ya no hay permiso para leerlo.', '', 'Elegir el archivo']
        : [`Último Maestro CASUR: <strong>${name}</strong>`, `Archivo del ${esc(modified)}${action === 'permission' ? ' · el navegador pedirá confirmar el acceso.' : ''}`, 'Recuperar Maestro', 'Elegir otro archivo'];
    return `<div class="master-restore">${icon('layers')}<p>${title}<small>${detail}</small></p><div class="master-restore-actions">${primary ? `<button type="button" class="button button-primary" data-master-restore="recover">${primary}</button>` : ''}<button type="button" class="button" data-master-restore="choose">${secondary}</button><button type="button" class="link-button" data-master-restore="dismiss">Ahora no</button></div></div>`;
  }

  private renderMasterRestore(): void { this.renderMapToast(this.store.getState()); }

  /** The card on the empty map: an error, the remembered Maestro (masterRestoreHtml) or how to load one. */
  private renderMapToast(state: AppState): void {
    const toast = required<HTMLElement>('#map-toast');
    if (Date.now() < this.notifyUntil && !state.error) return;
    const restore = state.error ? '' : this.masterRestoreHtml();
    toast.hidden = Boolean(state.layers.length) && !state.error;
    toast.classList.toggle('is-error', Boolean(state.error));
    toast.classList.toggle('is-transient', Boolean(state.error) && Boolean(state.layers.length));
    toast.classList.toggle('has-restore', Boolean(restore));
    const html = state.error ? `<strong>Error:</strong> ${esc(state.error)}` : restore || `<button type="button" class="button button-primary" data-master-quick="load">${icon('layers')}Cargar Maestro CASUR</button><span>o arrastra aquí un KML, KMZ, GeoJSON, Shapefile o GeoPackage.</span>`;
    if (toast.dataset.html !== html || toast.dataset.mode !== 'card') { toast.innerHTML = html; toast.dataset.html = html; toast.dataset.mode = 'card'; }
  }

  private async chooseMasterSource(): Promise<void> {
    const browser = window as unknown as MasterSourceBrowser;
    if (!supportsMasterSourcePicker(browser)) {
      this.clearPendingMasterSource();
      this.masterSourceAvailability = this.rememberedMasterSource ? 'unavailable' : 'unsupported';
      this.masterSourceError = 'Este navegador no permite recordar archivos. Usa Importar datos o abre GeoStudio en Chromium.';
      this.renderMasterSource();
      return;
    }
    try {
      const handle = await pickMasterSource(browser);
      if (!handle) {
        this.clearPendingMasterSource();
        return;
      }
      const file = await handle.getFile();
      this.beginImport([file], { handle, file });
    } catch (error) {
      this.clearPendingMasterSource();
      if (isPickerCancellation(error)) return;
      this.masterSourceError = error instanceof Error ? error.message : 'No fue posible seleccionar la fuente del Maestro CASUR.';
      this.renderMasterSource();
    }
  }

  private async loadRememberedMasterSource(): Promise<void> {
    const source = this.rememberedMasterSource;
    if (!source) return;
    if (this.store.getState().model && !window.confirm('Ya hay un Maestro CASUR activo. Continuar abrirá una confirmación antes de reemplazarlo.')) return;
    const result = await readMasterSource(source, true);
    this.masterSourceAvailability = result.status;
    this.masterSourceChanged = result.status === 'ready' && result.changed;
    this.masterSourceError = result.status === 'unavailable' ? result.error ?? null : null;
    this.renderMasterSource();
    if (result.status !== 'ready') return;
    this.beginImport([result.file], { handle: source.handle, file: result.file });
  }

  private async rememberLoadedMasterSource(): Promise<void> {
    const handle = this.pendingMasterSourceHandle;
    const file = this.pendingMasterSourceFile;
    if (!handle || !file) return;
    const source = createRememberedMasterSource(handle, file, undefined, this.pendingMasterSourceLayer ?? undefined);
    try {
      await this.masterSourceRepository.save(source);
      this.rememberedMasterSource = source;
      this.masterSourceAvailability = 'ready';
      this.masterSourceChanged = false;
      this.masterSourceError = null;
    } catch (error) {
      this.masterSourceError = error instanceof Error ? error.message : 'El Maestro se cargó, pero no fue posible recordar su fuente.';
    }
    this.renderMasterSource();
  }

  private showShapefileCrsDialog(datasets: readonly CrsPendingDataset[], message: string): void {
    renderCrsChoices(required('#shapefile-crs-list'), datasets);
    required<HTMLButtonElement>('#apply-shapefile-crs').disabled = true;
    required('#shapefile-crs-message').textContent = message;
    required<HTMLDialogElement>('#shapefile-crs-dialog').showModal();
  }

  private async importFiles(files: readonly File[], crsByDataset?: Readonly<Record<string, ManualShapefileCrs>>): Promise<void> {
    this.store.update({ loadingMessage: 'Inspeccionando geometrías y atributos…', error: null });
    await new Promise<void>((resolve) => window.setTimeout(resolve, 30));
    try {
      const geoPackageFiles = files.filter((file) => /\.gpkg$/i.test(file.name));
      if (geoPackageFiles.length) {
        if (files.length !== 1) throw new Error('Selecciona un solo GeoPackage por operación.');
        const inspection = await inspectGeoPackage(geoPackageFiles[0]!);
        this.pendingGeoPackageFile = geoPackageFiles[0]!;
        this.renderGeoPackageChoices(inspection);
        this.store.update({ loadingMessage: null, error: null });
        required<HTMLDialogElement>('#geopackage-layer-dialog').showModal();
        return;
      }
      const shapefileInput = files.some((file) => /\.(zip|shp|dbf|shx|prj|cpg)$/i.test(file.name));
      if (!shapefileInput && files.length !== 1) throw new Error('Selecciona un solo GeoJSON, KML o KMZ por operación.');
      if (shapefileInput) {
        const inspection = await inspectShapefiles(files, crsByDataset ? { crsByDataset } : {});
        this.store.update({ loadingMessage: null });
        this.chooseImportRole(inspection.datasets.map(({ layer }) => layer), new Map(inspection.datasets.map(({ layer, summary }) => [layer.id, summary])), inspection.notices);
        return;
      }
      const imported = [await importVectorLayer(files[0]!)];
      this.store.update({ loadingMessage: null });
      this.chooseImportRole(imported);
    } catch (error) {
      if (error instanceof ShapefileCrsRequiredError) {
        this.pendingShapefileFiles = [...files]; this.pendingShapefileCrs = crsByDataset ?? {};
        this.store.update({ loadingMessage: null, error: null });
        this.showShapefileCrsDialog(error.datasets, error.message);
        return;
      }
      this.store.update({ loadingMessage: null, error: error instanceof Error ? error.message : 'No fue posible importar la fuente.' });
      this.clearPendingMasterSource();
    }
  }

  private renderGeoPackageChoices(inspection: GeoPackageInspection): void {
    required('#geopackage-selection-message').textContent = `${inspection.layers.length} capa(s) vectorial(es) disponibles en ${inspection.fileName}.`;
    const list = required('#geopackage-layer-list');
    list.replaceChildren(...inspection.layers.map((layer) => {
      const label = document.createElement('label');
      label.className = 'gpkg-layer-choice';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'geopackage-layer';
      input.checked = inspection.layers[0] === layer;
      input.value = layer.tableName;
      const text = document.createElement('span');
      const title = document.createElement('strong');
      title.textContent = layer.displayName;
      const metadata = document.createElement('small');
      metadata.textContent = `${layer.geometryType} · ${layer.featureCount.toLocaleString('es-NI')} objetos · ${layer.sourceCrs}`;
      const fields = document.createElement('small');
      const names = layer.fields.map(({ name }) => name);
      fields.textContent = `${names.slice(0, 6).join(', ')}${names.length > 6 ? ` +${names.length - 6}` : ''}`;
      text.append(title, metadata, fields);
      label.append(input, text);
      return label;
    }));
  }

  private async loadGeoPackageLayers(file: File, tableNames: readonly string[]): Promise<void> {
    // A remembered GeoPackage Maestro also remembers its table, so it can be recovered without this dialog.
    this.pendingMasterSourceLayer = this.pendingMasterSourceHandle && tableNames.length === 1 ? tableNames[0]! : null;
    this.store.update({ loadingMessage: 'Cargando capas GeoPackage…', error: null });
    await new Promise<void>((resolve) => window.setTimeout(resolve, 30));
    try {
      const imported = await importGeoPackage(file, tableNames);
      this.store.update({ loadingMessage: null });
      this.chooseImportRole(imported);
    } catch (error) {
      this.store.update({ loadingMessage: null, error: error instanceof Error ? error.message : 'No fue posible cargar el GeoPackage.' });
      this.clearPendingMasterSource();
    }
  }

  private chooseImportRole(imported: readonly VectorLayerModel[], summaries: ReadonlyMap<string, ShapefileDatasetSummary> = new Map(), notices: readonly string[] = []): void {
    this.pendingImportedLayers = [...imported]; this.pendingImportSummaries = summaries; this.pendingImportNotices = notices;
    const masterRadio = required<HTMLInputElement>('#import-role-dialog input[value="master"]');
    const eligibility = imported.length === 1 ? masterEligibility(imported[0]!) : { eligible: false, reason: 'Selecciona una sola capa para usarla como Maestro CASUR.' };
    masterRadio.disabled = !eligibility.eligible;
    masterRadio.checked = eligibility.eligible && (Boolean(this.pendingMasterSourceHandle) || this.masterIntent || !this.store.getState().model);
    required<HTMLInputElement>('#import-role-dialog input[value="additional"]').checked = !masterRadio.checked;
    const rememberOption = required<HTMLElement>('#remember-master-source-option');
    const rememberInput = required<HTMLInputElement>('#remember-master-source');
    rememberOption.hidden = !this.pendingMasterSourceHandle;
    rememberInput.checked = Boolean(this.pendingMasterSourceHandle);
    rememberInput.disabled = !masterRadio.checked;
    required('#import-role-message').textContent = imported.length === 1
      ? `${imported[0]!.name} · ${imported[0]!.features.length.toLocaleString('es-NI')} geometría(s)`
      : `${imported.length} capas listas para agregar.`;
    required('#import-role-validation').textContent = eligibility.reason;
    this.renderImportSummary();
    required<HTMLDialogElement>('#import-role-dialog').showModal();
  }

  /** Summary of what will be added (type, records, CRS origin, DBF, warnings) and a primary action that says it. */
  private renderImportSummary(): void {
    const imported = this.pendingImportedLayers; if (!imported.length) return;
    const master = document.querySelector<HTMLInputElement>('#import-role-dialog input[value="master"]')?.checked === true;
    required('#import-summary').innerHTML = importSummaryHtml(imported, this.pendingImportSummaries, this.pendingImportNotices, master ? 'Maestro CASUR (reemplaza el actual)' : 'capa vectorial adicional');
    required('#apply-import-role').textContent = master ? 'Usar como Maestro CASUR' : imported.length === 1 ? 'Agregar capa al mapa' : `Agregar ${imported.length} capas al mapa`;
  }

  /**
   * Makes a vector layer the read-only Maestro CASUR (import dialog and recovery of the remembered source alike).
   * It replaces a previous Maestro and the analysis that depended on it; external and planning layers are kept, the
   * source geometry is never modified, and a new undo history starts. `markDirty: false` for an automatic recovery.
   */
  private applyMasterLayer(layer: VectorLayerModel, options: { readonly markDirty?: boolean } = {}): void {
    const sourceModel = masterModelFromVectorLayer(layer);
    const geometryEdits = emptyGeometryEditHistory();
    const model = deriveActiveLayerModel(sourceModel, geometryEdits);
    const state = this.store.getState();
    const existing = state.layers.filter((item) => item.kind !== 'master' && item.kind !== 'subarea');
    const layers = withActiveMasterModel(normalizeLayerOrder([createMasterLayer(sourceModel), ...existing]), model);
    const classificationConfig = {
      ...defaultClassificationConfig,
      layerId: model.id,
      remainderColor: state.styleConfig.fillColor,
      singleStyle: {
        fillColor: state.styleConfig.fillColor,
        outlineColor: state.styleConfig.outlineColor,
        fillOpacity: state.styleConfig.fillOpacity,
        outlineWidth: state.styleConfig.outlineWidth,
      },
    };
    this.store.update({ sourceModel, model, geometryEdits, geometryEditSession: null, layers, activeLayerId: model.id, activeTool: 'select', genericSelection: null, selectionSet: emptySelectionSet(), loadingMessage: null, error: null, selection: null, activeFincaId: null, viewScope: 'all', workScope: emptyWorkScope(), classificationConfig, thematicSchemes: [], legendConfig: defaultLegendConfig, savedSelections: [], operationalFields: [], subareas: [], attributeFilters: [] }, { history: 'reset', ...(options.markDirty === false ? { markDirty: false } : {}) });
  }

  private async applyPendingImport(): Promise<void> {
    const imported = this.pendingImportedLayers;
    if (!imported.length) return;
    const role = document.querySelector<HTMLInputElement>('input[name="import-role"]:checked')?.value;
    const rememberSource = role === 'master' && required<HTMLInputElement>('#remember-master-source').checked;
    required<HTMLDialogElement>('#import-role-dialog').close();
    const summaries = this.pendingImportSummaries;
    this.pendingImportedLayers = []; this.pendingImportSummaries = new Map(); this.pendingImportNotices = [];
    if (role === 'master') {
      try {
        this.applyMasterLayer(imported[0]!);
        if (rememberSource) await this.rememberLoadedMasterSource();
      } catch (error) {
        this.store.update({ error: error instanceof Error ? error.message : 'La fuente no puede usarse como Maestro CASUR.' });
      }
      this.clearPendingMasterSource();
      return;
    }
    const layers = normalizeLayerOrder([...this.store.getState().layers, ...imported]);
    const active = imported.at(-1)!;
    this.store.update({ layers, activeLayerId: active.id, activeTool: 'select', genericSelection: null, selection: null, selectionSet: emptySelectionSet(), error: null });
    this.mapController.zoomToLayer(active.id);
    this.clearPendingMasterSource();
    this.notify(importSuccessMessage(imported, summaries));
  }

  private renderMasterSource(): void {
    const source = this.rememberedMasterSource;
    const browserSupported = supportsMasterSourcePicker(window as unknown as MasterSourceBrowser);
    const status = required('#master-source-status');
    const metadata = required('#master-source-metadata');
    const warning = required<HTMLElement>('#master-source-warning');
    const emptyControls = required<HTMLElement>('#master-source-empty-controls');
    const presentControls = required<HTMLElement>('#master-source-present-controls');
    const load = required<HTMLButtonElement>('#load-master-source');
    const change = required<HTMLButtonElement>('#change-master-source');
    emptyControls.hidden = Boolean(source);
    presentControls.hidden = !source;
    if (!source) {
      status.textContent = this.masterSourceAvailability === 'loading' ? 'Buscando fuente recordada…' : 'Sin fuente recordada';
      metadata.textContent = browserSupported ? 'Selecciona un archivo compatible y valida que pueda usarse como Maestro.' : 'Este navegador no permite conservar un FileSystemFileHandle.';
    } else {
      status.textContent = `${source.metadata.name}${this.masterSourceAvailability === 'ready' ? '' : ' · requiere atención'}`;
      const size = source.metadata.size.toLocaleString('es-NI');
      const modified = new Date(source.metadata.lastModified).toLocaleString('es-NI');
      const loaded = new Date(source.metadata.lastLoadedAt).toLocaleString('es-NI');
      metadata.textContent = `${source.metadata.format} · ${size} bytes · modificado ${modified} · última carga ${loaded}`;
      load.textContent = this.masterSourceAvailability === 'permission-required' ? 'Autorizar y cargar' : this.masterSourceChanged ? 'Recargar Maestro' : 'Cargar Maestro';
      load.disabled = this.masterSourceAvailability === 'unavailable' || this.masterSourceAvailability === 'denied';
      change.textContent = this.masterSourceAvailability === 'unavailable' || this.masterSourceAvailability === 'denied' ? 'Volver a vincular' : 'Cambiar fuente';
      change.disabled = !browserSupported;
    }
    const messages = [
      this.masterSourceChanged ? 'La fuente del Maestro CASUR fue modificada desde la última carga.' : '',
      this.masterSourceAvailability === 'permission-required' ? 'El navegador solicitará permiso únicamente al pulsar “Autorizar y cargar”.' : '',
      this.masterSourceAvailability === 'denied' ? 'El permiso del archivo no está disponible. Vuelve a vincular la fuente.' : '',
      this.masterSourceAvailability === 'unavailable' ? 'El archivo ya no está disponible. Vuelve a vincularlo; se conservarán estos metadatos hasta validar la nueva fuente.' : '',
      this.masterSourceError ?? '',
    ].filter(Boolean);
    warning.textContent = messages.join(' ');
    warning.hidden = messages.length === 0;
    required<HTMLInputElement>('#master-autoload').checked = loadMasterAutoloadPreference();
    this.renderMasterQuick(this.store.getState());
    this.renderMasterRestore();
  }

  /** `selection`: only the selection changed (see selectionOnlyChange), so only the views that read it are refreshed. */
  private render(scope: 'full' | 'selection' = 'full'): void {
    const state = this.store.getState();
    const activeLayer = state.layers.find((layer) => layer.id === state.activeLayerId) ?? null;
    this.explorer.render(state.model, state.selection, state.model ? deriveDisplaySet(state).masterFragmentIds : undefined);
    this.propertiesPanel.render(activeLayer, state.selection, state.genericSelection, state.selectionSet, state.casurHydricReference, state.casurHydricSelection);
    // The table is only materialised while its dock is open (thousands of logical rows otherwise).
    if (state.workspaceLayout.attributeTableOpen) this.attributeTable.render(activeLayer, state.selection, state.genericSelection, state.selectionSet, state.workScope, activeLayer ? deriveDisplaySet(state).layerFeatureIds.get(activeLayer.id) : undefined, state.operationalFields, state.entityViewLevel, state.sourceModel);
    this.presentationEditor.render(state);
    this.workspaceController.render(state);
    const editTarget = state.model && !state.geometryEditSession ? geometryEditTargetForSelection(state.model, state.selection) : null;
    const editButton = required<HTMLButtonElement>('#tool-edit-geometry');
    editButton.disabled = !editTarget || state.selection?.kind === 'fragment';
    editButton.classList.toggle('active', Boolean(state.geometryEditSession));
    const selection = this.selectionSummary(state);
    this.statusBar.render({ level: state.entityViewLevel, visible: this.visibleEntityCount(state), hasMaster: Boolean(state.model), selected: selection.count, selectedAreaHa: selection.areaHa, selectedNoun: selection.noun });
    // Cheap (one Suerte) and selection-dependent: refreshed on selection-only renders too.
    this.suerteSplit.render(state);
    // Selection-dependent summary; computed only while the Exportar pane is showing (memoized by selection).
    this.topographyExport.render(state);
    if (scope === 'selection') return;
    this.layerManager.render(state.layers, state.activeLayerId);
    this.workingLayerPanel.render(state);
    this.planningPanel.render(state);
    this.scopePanel.render(state);
    this.mapLegend.render(state);
    this.subareaPanel.render(state);
    this.mapExportController.render();
    this.renderMasterSource();
    required<HTMLSelectElement>('#basemap-select').value = state.basemap;
    required<HTMLElement>('#hybrid-reference-controls').hidden = state.basemap !== 'hybrid';
    document.querySelectorAll<HTMLInputElement>('[data-hybrid-reference]').forEach((input) => {
      input.checked = state.hybridReference[input.dataset.hybridReference as keyof HybridReferenceVisibility];
    });
    document.querySelectorAll<HTMLInputElement>('[data-casur-hydric]').forEach((input) => {
      input.checked = state.casurHydricVisibility[input.dataset.casurHydric as keyof CasurHydricVisibility];
      input.disabled = !state.casurHydricReference || state.casurHydricLoading;
    });
    const hydricAvailable = Boolean(state.casurHydricReference);
    required('#casur-hydric-status').textContent = state.casurHydricLoading
      ? 'Procesando Referencia CASUR…'
      : state.casurHydricError ?? (hydricAvailable ? 'Referencia CASUR disponible' : 'Referencia CASUR no disponible');
    required<HTMLElement>('#casur-hydric-metadata').textContent = state.casurHydricReference
      ? `${state.casurHydricReference.metadata.sourceFileName} · ${new Date(state.casurHydricReference.metadata.importedAt).toLocaleString('es-NI')} · ${state.casurHydricReference.metadata.channelCount + state.casurHydricReference.metadata.drainageCount} líneas`
      : '';
    required<HTMLElement>('#casur-hydric-present-controls').hidden = !hydricAvailable;
    required<HTMLElement>('#casur-hydric-empty-controls').hidden = hydricAvailable;
    required<HTMLSelectElement>('#label-select').value = state.labelMode === 'suerte_tablones' || state.labelMode === 'seleccion' || state.labelMode === 'none' ? state.labelMode : 'entity';
    required<HTMLInputElement>('#labels-visible-toggle').checked = state.labelMode !== 'none';
    this.renderViewBy(state);
    this.breadcrumb.render(state);
    this.geometryEditToolbar.render(state);
    this.classifyPane.render(state);
    this.legendDesignPane.render(state);
    this.scenarioPane.render(state);
    this.filterBar.render(state);
    document.querySelectorAll<HTMLButtonElement>('[data-map-tool]').forEach((button) => button.classList.toggle('active', button.dataset.mapTool === state.activeTool));
    required('#move-labels').classList.toggle('active', state.activeTool === 'move-labels');
    const loading = required<HTMLElement>('#loading-overlay'); loading.hidden = !state.loadingMessage;
    required('#loading-message').textContent = state.loadingMessage ?? '';
    this.renderJoinList(state);
    this.renderMapToast(state);
    const status = required('#dataset-status');
    status.classList.toggle('ready', Boolean(state.layers.length));
    status.innerHTML = state.layers.length ? `<i></i>${state.layers.length} capa(s) · ${state.layers.reduce((sum, layer) => sum + layer.features.length, 0).toLocaleString('es-NI')} objetos` : '<i></i>Sin capa cargada';
    required('#appbar-project-name').textContent = state.project.name;
  }

  private visibleCountCache: { readonly ids: ReadonlySet<string>; readonly level: EntityViewLevel; readonly count: number } | null = null;
  private visibleEntityCount(state: ReturnType<AppStore['getState']>): number {
    if (!state.model) return 0;
    const ids = deriveDisplaySet(state).masterFragmentIds;
    if (this.visibleCountCache?.ids === ids && this.visibleCountCache.level === state.entityViewLevel) return this.visibleCountCache.count;
    const count = countEntitiesForFragments(state.model, state.entityViewLevel, ids);
    this.visibleCountCache = { ids, level: state.entityViewLevel, count };
    return count;
  }

  private selectionSummaryCache: { readonly key: unknown; readonly layers: unknown; readonly value: { count: number; areaHa: number; noun: string } } | null = null;
  /** Status-bar figures from the same summaries as Propiedades: deduplicated active area, polygons only for other layers. */
  private selectionSummary(state: ReturnType<AppStore['getState']>): { count: number; areaHa: number; noun: string } {
    if (this.selectionSummaryCache?.key === state.selectionSet && this.selectionSummaryCache.layers === state.layers) return this.selectionSummaryCache.value;
    const layer = state.layers.find(({ id }) => id === state.selectionSet.layerId);
    const count = state.selectionSet.entries.length;
    const masterKind = state.selectionSet.entries[0]?.source === 'master' ? state.selectionSet.entries[0].kind : null;
    const noun = masterKind ? entityNoun(masterKind, count).toLocaleLowerCase('es') : count === 1 ? 'objeto' : 'objetos';
    const model = layer?.kind === 'master' ? layer.masterModel ?? null : null;
    const areaHa = !layer || !count ? 0 : model ? summarizeMasterSelection(model, state.selectionSet).totalAreaHa : summarizeFeatureSelection(layer, state.selectionSet).totalAreaHa ?? 0;
    const value = { count, areaHa, noun };
    this.selectionSummaryCache = { key: state.selectionSet, layers: state.layers, value };
    return value;
  }

  private renderViewBy(state: ReturnType<AppStore['getState']>): void {
    document.querySelectorAll<HTMLButtonElement>('[data-view-level]').forEach((button) => {
      const level = button.dataset.viewLevel as EntityViewLevel;
      button.setAttribute('aria-pressed', String(level === state.entityViewLevel));
      button.disabled = !state.model;
      if (level === 'fragment') button.hidden = state.entityViewLevel !== 'fragment';
    });
  }

  /**
   * Frames what is visible now without changing selection, filters, scope or classification: the Maestro DisplaySet
   * (WorkScope + filters + EntityView + active geometry), or the active layer when no Maestro is loaded.
   */
  private zoomToVisible(): void {
    const state = this.store.getState();
    const framed = state.model ? this.mapController.zoomToScope() : Boolean(state.activeLayerId) && this.mapController.zoomToLayer(state.activeLayerId!);
    if (!framed) this.notify('No hay entidades visibles para encuadrar');
  }

  /** Marks the single temporary location and shows its card in Properties; never touches the agricultural selection. */
  private markLocation(location: GeoCoordinate, inspect: boolean): void {
    this.mapLocation.mark(location, { inspect });
    this.shellController.openPane('properties');
    document.querySelector<HTMLElement>('.dock-right .dock-scroll')?.scrollTo({ top: 0 });
  }

  private async copyCoordinate(location: GeoCoordinate): Promise<void> {
    this.notify(await copyText(formatCoordinate(location)) ? 'Coordenada copiada' : 'No se pudo copiar la coordenada');
  }

  /** The map menu only opens for navigation/selection; drawing, measuring, label moving and Difference keep the right button. */
  private canOpenMapContextMenu(): boolean {
    const tool = this.store.getState().activeTool;
    return (tool === 'navigate' || tool === 'select' || tool === 'select-multiple' || tool === 'select-box')
      && !this.differenceDrawMode && !this.pendingBoundaryCutPlan;
  }

  private runMapContextAction(action: MapContextAction, coordinate: GeoCoordinate): void {
    if (action === 'copy') void this.copyCoordinate(coordinate);
    else if (action === 'mark') this.markLocation(coordinate, false);
    else this.mapLocation.centerOn(coordinate);
  }

  private notifyTimer = 0;
  private notifyUntil = 0;
  /** Short non-blocking confirmation over the map. */
  private notify(message: string): void {
    const toast = required<HTMLElement>('#map-toast');
    toast.hidden = false; toast.classList.remove('is-error', 'has-restore'); toast.classList.add('is-transient'); toast.textContent = message; toast.dataset.mode = 'message';
    window.clearTimeout(this.notifyTimer);
    this.notifyUntil = Date.now() + 3500;
    this.notifyTimer = window.setTimeout(() => this.render(), 3600);
  }

  private renderJoinList(state: ReturnType<AppStore['getState']>): void {
    const list = required<HTMLElement>('#excel-join-list');
    const key = state.excelJoins.length;
    if (list.dataset.count === String(key)) return;
    list.dataset.count = String(key);
    const plural: Record<string, string> = { finca: 'Fincas', suerte: 'Suertes', tablon: 'Tablones', fragment: 'Fragmentos' };
    list.innerHTML = state.excelJoins.length ? state.excelJoins.map((join) => `<div><strong>${esc(join.fileName)} · ${esc(join.sheet)}</strong><small>${join.audit.matched.toLocaleString('es-NI')} ${plural[join.targetEntityLevel ?? join.level] ?? ''} · ${join.selectedColumns.map(esc).join(', ')}</small></div>`).join('') : '<small>Sin relaciones.</small>';
  }

  /** «Mover etiqueta en el mapa»: on a phone the sheet steps aside so the map can be dragged; «Listo» or Esc end it. */
  private setLabelDrag(onDrag: ((dx: number, dy: number, done: boolean) => void) | null): void {
    this.labelDrag.set(onDrag);
    const bar = required<HTMLElement>('#label-move-bar'); const shell = required<HTMLElement>('.app-shell');
    bar.hidden = !onDrag;
    if (onDrag && window.matchMedia?.('(max-width: 760px)').matches) { bar.dataset.reopenSheet = shell.dataset.mobileDock ?? ''; delete shell.dataset.mobileDock; }
    if (!onDrag && bar.dataset.reopenSheet) { shell.dataset.mobileDock = bar.dataset.reopenSheet; delete bar.dataset.reopenSheet; }
  }

  private openAttributeTable(): void { this.layoutController.openTable(); }

  /** «Ver en tabla»: the selection's own layer, expanded, showing only the selected rows (one click on «Solo selección» shows all). */
  private showSelectionInTable(): void {
    const state = this.store.getState();
    const layerId = state.selectionSet.layerId;
    if (layerId && layerId !== state.activeLayerId && state.layers.some(({ id }) => id === layerId)) this.store.update({ activeLayerId: layerId }, { markDirty: false });
    this.attributeTable.setSelectedOnly(state.selectionSet.entries.length > 0);
    this.layoutController.openTable();
  }

  /** Exports the visible entities of the active level (or one layer) as GeoJSON (WGS 84) or CSV. */
  private runDataExport(format: 'geojson' | 'csv', layerId?: string): void {
    const state = this.store.getState();
    const layer = layerId ? state.layers.find(({ id }) => id === layerId) : undefined;
    const set = layer ? layerExportSet(state, layer) : masterExportSet(state);
    if (!set || !set.records.length) { this.store.update({ error: 'No hay entidades visibles para exportar.' }, { markDirty: false }); return; }
    const content = format === 'geojson' ? toGeoJson(set) : toCsv(set);
    const blob = new Blob([content], { type: format === 'geojson' ? 'application/geo+json' : 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob); const anchor = document.createElement('a');
    anchor.href = url; anchor.download = `${safeFileName(set.name)}.${format === 'geojson' ? 'geojson' : 'csv'}`; document.body.append(anchor); anchor.click(); anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
    const message = `${set.records.length.toLocaleString('es-NI')} entidades exportadas · ${anchor.download}`;
    required('#data-export-status').textContent = message;
    this.notify(message);
  }

  /** Normal labeling follows "Ver por"; only the advanced composition chooses special modes. */
  private setLabelComposition(value: string): void {
    const state = this.store.getState();
    const level = state.entityViewLevel;
    if (value === 'entity') { this.store.update({ labelMode: labelModeForLevel(level), labelConfig: alignLabelConfigToEntity(state.labelConfig, level) }); return; }
    if (value === 'suerte_tablones') { this.store.update({ labelMode: 'suerte_tablones', labelConfig: { ...state.labelConfig, target: 'suerte' } }); return; }
    if (value === 'seleccion') { this.store.update({ labelMode: 'seleccion', labelConfig: { ...state.labelConfig, target: 'selection' } }); return; }
    this.store.update({ labelMode: 'none' });
  }

  private runCommand(command: ShellCommand, _source: HTMLElement): void {
    if (command === 'import-master') { if (this.rememberedMasterSource) void this.loadRememberedMasterSource(); else void this.chooseMasterSource(); return; }
    if (command === 'import-hydric') { required<HTMLInputElement>('#casur-hydric-file-input').click(); return; }
    if (command === 'edit-geometry') { this.handleGeometryAction('begin'); return; }
    if (command === 'insert-text') { this.shellController.openPane('planning'); this.planningPanel.start('text'); return; }
    if (command === 'focus-search') { const input = required<HTMLInputElement>('#entity-search'); input.focus(); input.select(); return; }
    if (command === 'filter-open') { this.filterBar.open(); return; }
    // Help is a lazy chunk: its content, DOM and listeners exist only while it is open.
    if (command === 'practical-cases') {
      void import('./help/PracticalCasesView').then(({ openPracticalCases }) => openPracticalCases()).catch(() => this.notify('No fue posible abrir Casos prácticos. Revisa la conexión e inténtalo de nuevo.'));
      return;
    }
    if (command === 'topography-reference') {
      this.shellController.openPane('export');
      document.querySelector('#topo-ref')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      this.topographyExport.render(this.store.getState());
      return;
    }
    if (command === 'export-geojson' || command === 'export-csv') this.runDataExport(command === 'export-geojson' ? 'geojson' : 'csv');
  }

  private setEntityViewLevel(level: EntityViewLevel, workScope = this.store.getState().workScope): void {
    const state = this.store.getState();
    const labelMode: LabelMode = state.labelMode === 'none' ? 'none' : labelModeForLevel(level);
    const labelTarget = level === 'finca' ? 'finca' : level === 'suerte' ? 'suerte' : level === 'tablon' ? 'tablon' : 'fragment';
    let classificationConfig = state.classificationConfig;
    const masterLayer = state.layers.find((layer) => layer.kind === 'master' && layer.id === state.model?.id);
    if (state.model && masterLayer && classificationConfig.layerId === masterLayer.id) {
      classificationConfig = { ...classificationConfig, level };
      if (classificationConfig.field) classificationConfig = createMasterClassification(state.model, classificationConfig, themeTargetIds(masterLayer, classificationConfig.themeTarget), state.operationalFields);
    }
    this.store.update({ entityViewLevel: level, selectionLevel: level, workScope, labelMode, labelConfig: alignLabelConfigToEntity(state.labelConfig, labelTarget), classificationConfig, thematicSchemes: state.thematicSchemes.map((item) => item.id === classificationConfig.id ? classificationConfig : item), selectionSet: emptySelectionSet(), selection: null, genericSelection: null });
  }

  /** Drill-down / breadcrumb navigation: changes only level + WorkScope; the left-behind entity stays selected. */
  private navigateTo(target: NavigationTarget): void {
    const state = this.store.getState(); if (!state.model) return;
    if (state.geometryEditSession) this.cancelGeometryEdit();
    this.setEntityViewLevel(target.level, target.scope);
    if (target.focus) this.selectionController.focusEntry({ source: 'master', layerId: state.model.id, kind: target.focus.kind, id: target.focus.id }, false);
  }

  private zoomToCurrentSelection(): void {
    if (this.store.getState().selectionSet.entries.length) this.selectionController.zoomToSelection(); else this.mapController.zoomToSelection();
  }

  /** Selects an entity (search result, parent link); "Ver por" follows the entity level. */
  private focusEntity(kind: EntityViewLevel, id: string, zoom = false): void {
    const state = this.store.getState(); if (!state.model) return;
    if (state.geometryEditSession) this.cancelGeometryEdit();
    const fragmentIds = entityViewFragmentIds(state.model, kind, id);
    const visible = deriveDisplaySet(state).masterFragmentIds;
    const inScope = workScopeFragmentIds(state.model, state.workScope);
    // A result outside the current hierarchical scope reopens the whole CASUR; filters are never removed silently.
    const scope = fragmentIds.some((fragmentId) => inScope.has(fragmentId)) ? state.workScope : emptyWorkScope();
    if (state.entityViewLevel !== kind || scope !== state.workScope) this.setEntityViewLevel(kind, scope);
    if (!fragmentIds.some((fragmentId) => visible.has(fragmentId)) && scope === state.workScope) this.store.update({ error: 'La entidad está oculta por los filtros activos; quita un filtro para verla en el mapa.' }, { markDirty: false });
    this.selectionController.focusEntry({ source: 'master', layerId: state.model.id, kind, id }, false);
    if (zoom) this.selectionController.zoomToSelection();
  }

  private runSelectionAction(action: 'assign' | 'classify' | 'label' | 'save' | 'partial' | 'clear' | 'table'): void {
    const state = this.store.getState();
    if (action === 'assign') { this.presentationEditor.focusAttributeAssignment(); return; }
    if (action === 'classify') { this.presentationEditor.openClassificationForSelection(); return; }
    if (action === 'label') {
      const level = state.entityViewLevel;
      this.store.update({ labelMode: labelModeForLevel(level), labelConfig: { ...alignLabelConfigToEntity(state.labelConfig, level), labelTarget: captureLabelTarget(state, 'selection') } });
      this.shellController.openPane('labels');
      return;
    }
    if (action === 'save') {
      const name = window.prompt('Nombre del grupo', `Selección ${state.savedSelections.length + 1}`)?.trim();
      if (!name || !state.selectionSet.layerId || !state.selectionSet.entries.length) return;
      this.store.update({ savedSelections: [...state.savedSelections, { id: `selection-${Date.now()}`, name, layerId: state.selectionSet.layerId, entries: state.selectionSet.entries.map((entry) => ({ ...entry })), createdAt: new Date().toISOString() }] });
      return;
    }
    if (action === 'partial') { this.shellController.openPane('subareas'); this.partialAreaController.begin('draw-polygon'); return; }
    if (action === 'table') { this.showSelectionInTable(); return; }
    this.selectionController.clear();
  }

  private drillDown(kind: EntityViewLevel, id: string): void {
    const state = this.store.getState(); if (!state.model) return;
    const target = drillDownTarget(state.model, kind, id);
    if (target) this.navigateTo(target);
  }

  private activateTool(tool: MapTool): void {
    this.elevationQuery.deactivate(false);
    if (this.store.getState().geometryEditSession && tool !== 'select') this.cancelGeometryEdit();
    if (!this.drawingController.canActivate(tool)) {
      this.drawingController.setTool(tool);
      return;
    }
    this.store.update({ activeTool: tool });
  }
}
